import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  addDoc, 
  deleteDoc, 
  orderBy,
  limit 
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './App.css';

// Simple confetti animation CSS
const confettiStyles = `
  @keyframes confetti {
    0% {
      transform: translateY(-100vh) rotate(0deg);
      opacity: 1;
    }
    100% {
      transform: translateY(100vh) rotate(360deg);
      opacity: 0;
    }
  }
  
  .animate-confetti {
    animation: confetti linear forwards;
  }
`;

// Add styles to document
if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = confettiStyles;
  document.head.appendChild(styleSheet);
}

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const storage = getStorage(app);

// Helper function to capitalize a string
const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Current quarter from calendar: Q1 = Jan–Mar, Q2 = Apr–Jun, Q3 = Jul–Sep, Q4 = Oct–Dec.
const getCurrentQuarter = () => {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `Q${q}-${now.getFullYear()}`;
};

// Quarter to use for data and display: from settings, or current if not set or if stored quarter has ended.
const getEffectiveQuarter = (settings) => {
  const stored = settings?.currentQuarter;
  if (!stored || !stored.trim()) return getCurrentQuarter();
  const m = stored.match(/Q(\d)-(\d+)/);
  if (!m) return stored;
  const qNum = parseInt(m[1], 10);
  const year = parseInt(m[2], 10);
  const now = new Date();
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth() + 1;
  const currentQ = Math.ceil(nowMonth / 3);
  const currentYear = nowYear;
  if (year < currentYear || (year === currentYear && qNum < currentQ)) return getCurrentQuarter();
  return stored;
};

// Return human-readable date when the next quarter starts (e.g. "1. aprila 2026.").
const getNextQuarterStartText = (quarterId) => {
  if (!quarterId) return '';
  const m = quarterId.match(/Q(\d)-(\d+)/);
  if (!m) return '';
  const qNum = parseInt(m[1], 10);
  const year = parseInt(m[2], 10);
  const nextQ = qNum < 4 ? qNum + 1 : 1;
  const nextYear = qNum < 4 ? year : year + 1;
  const month = (nextQ - 1) * 3 + 1;
  const d = new Date(nextYear, month - 1, 1);
  const monthNames = ['januara', 'februara', 'marta', 'aprila', 'maja', 'juna', 'jula', 'avgusta', 'septembra', 'oktobra', 'novembra', 'decembra'];
  return `${d.getDate()}. ${monthNames[d.getMonth()]} ${d.getFullYear()}.`;
};

// Ensure a quarter document exists in Firestore so the quarter appears in the console and subcollections (activities, acceptedChallenges, points) can be used.
const ensureQuarterExists = async (database, quarterId) => {
  if (!quarterId) return;
  const quarterRef = doc(database, 'quarters', quarterId);
  const snap = await getDoc(quarterRef);
  if (!snap.exists()) {
    await setDoc(quarterRef, { id: quarterId, createdAt: new Date().toISOString() });
  }
};

// Helper function to get display name from member (supports old and new format)
const getDisplayName = (member) => {
  if (!member) return '';
  
  // New format: has separate name, surname1, surname2
  if (member.surname1 || member.surname2) {
    const parts = [member.name];
    if (member.surname1) parts.push(member.surname1);
    if (member.surname2) parts.push(member.surname2);
    return parts.map(capitalize).join(' ');
  }
  
  // Old format: use name field
  return member.name || '';
};

// Delete a member and all their data (points, activities, victories, acceptedChallenges, challengeProofs). Use for "Nepoznat član" or when admin removes a member.
// currentQuarter is required so we always delete the points doc (quarters collection may be empty in Firestore).
const deleteMemberAndAllData = async (database, memberId, currentQuarter) => {
  if (!memberId) return;
  const processQuarter = async (quarterId) => {
    const pointsRef = doc(database, 'quarters', quarterId, 'points', memberId);
    await deleteDoc(pointsRef).catch(() => {});
    const activitiesSnap = await getDocs(query(collection(database, 'quarters', quarterId, 'activities'), where('memberId', '==', memberId)));
    for (const d of activitiesSnap.docs) await deleteDoc(d.ref);
    const acceptedSnap = await getDocs(query(collection(database, 'quarters', quarterId, 'acceptedChallenges'), where('memberId', '==', memberId)));
    for (const d of acceptedSnap.docs) await deleteDoc(d.ref);
    const proofsSnap = await getDocs(query(collection(database, 'quarters', quarterId, 'challengeProofs'), where('memberId', '==', memberId)));
    for (const d of proofsSnap.docs) await deleteDoc(d.ref);
  };
  if (currentQuarter) await processQuarter(currentQuarter);
  const quartersSnap = await getDocs(collection(database, 'quarters'));
  for (const quarterDoc of quartersSnap.docs) {
    const quarterId = quarterDoc.id;
    if (quarterId === currentQuarter) continue;
    await processQuarter(quarterId);
  }
  const victoriesSnap = await getDocs(query(collection(database, 'victories'), where('memberId', '==', memberId)));
  for (const d of victoriesSnap.docs) await deleteDoc(d.ref);
  const memberRef = doc(database, 'members', memberId);
  await deleteDoc(memberRef).catch(() => {});
};

// Sanitize HTML for safe display (allow formatting tags and safe links).
// Builds a new tree instead of mutating to avoid replaceChild on detached nodes.
const ALLOWED_HTML_TAGS = ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'span', 'a'];
const sanitizeHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  if (typeof document === 'undefined') return html.replace(/<[^>]+>/g, '');
  const div = document.createElement('div');
  div.innerHTML = html;
  const process = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return document.createTextNode(node.textContent || '');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const tag = node.tagName.toLowerCase();
    if (!ALLOWED_HTML_TAGS.includes(tag)) {
      return document.createTextNode(node.textContent || '');
    }
    const out = document.createElement(tag);
    if (tag === 'a') {
      const href = node.getAttribute('href') || '';
      const safeHref = href.startsWith('http://') || href.startsWith('https://') ? href : (href ? 'https://' + href.replace(/^\/+/, '') : '#');
      out.setAttribute('href', safeHref);
      out.setAttribute('target', '_blank');
      out.setAttribute('rel', 'noopener noreferrer');
    }
    for (let i = 0; i < node.childNodes.length; i++) {
      const child = process(node.childNodes[i]);
      if (child) out.appendChild(child);
    }
    return out;
  };
  const fragment = document.createDocumentFragment();
  for (let i = 0; i < div.childNodes.length; i++) {
    const child = process(div.childNodes[i]);
    if (child) fragment.appendChild(child);
  }
  const outDiv = document.createElement('div');
  outDiv.appendChild(fragment);
  return outDiv.innerHTML;
};

// Rich text editor for admin (bold, italic, underline, paragraphs)
function RichTextEditor({ value, onChange, placeholder, className = '' }) {
  const editorRef = React.useRef(null);

  React.useEffect(() => {
    if (editorRef.current && value !== undefined && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current && onChange) onChange(editorRef.current.innerHTML);
  };

  const execCmd = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const insertParagraph = () => {
    execCmd('insertHTML', '<p><br></p>');
  };

  const insertLink = () => {
    editorRef.current?.focus();
    const sel = window.getSelection();
    const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
    const hasSelection = range && !range.collapsed;
    const url = window.prompt('Unesite URL linka:', 'https://');
    if (url == null || url.trim() === '') return;
    const href = url.trim().startsWith('http') ? url.trim() : `https://${url.trim()}`;
    if (hasSelection) {
      document.execCommand('createLink', false, href);
      let node = sel.anchorNode;
      while (node && node !== editorRef.current) {
        if (node.nodeName === 'A') {
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer');
          break;
        }
        node = node.parentNode;
      }
    } else {
      const text = window.prompt('Tekst linka:', '');
      if (text == null) return;
      const safeHref = href.replace(/"/g, '&quot;');
      const safeText = (text || href).replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const linkHtml = '<a href="' + safeHref + '" target="_blank" rel="noopener noreferrer">' + safeText + '</a>';
      execCmd('insertHTML', linkHtml);
    }
    handleInput();
  };

  return (
    <div className={`border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[#931b1d] focus-within:border-transparent ${className}`}>
      <div className="flex flex-wrap gap-1 p-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <button type="button" onClick={() => execCmd('bold')} className="px-2 py-1 text-sm font-bold border border-gray-300 rounded hover:bg-gray-200" title="Podebljano">B</button>
        <button type="button" onClick={() => execCmd('italic')} className="px-2 py-1 text-sm italic border border-gray-300 rounded hover:bg-gray-200" title="Kurziv">I</button>
        <button type="button" onClick={() => execCmd('underline')} className="px-2 py-1 text-sm underline border border-gray-300 rounded hover:bg-gray-200" title="Podvučeno">U</button>
        <button type="button" onClick={insertLink} className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 flex items-center gap-0.5" title="Umetni link">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
          <span className="sr-only">Link</span>
        </button>
        <button type="button" onClick={insertParagraph} className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200" title="Novi pasus">¶</button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="min-h-[120px] px-4 py-3 text-gray-800 text-sm focus:outline-none rounded-b-lg prose prose-sm max-w-none"
        data-placeholder={placeholder}
        style={{ outline: 'none' }}
      />
    </div>
  );
}

// Render micro-action body: formatted description, "Dodatni materijali" link, tags
function MicroActionBody({ description, materialLink, tags, className = '', tagStyle }) {
  const isHtml = description && typeof description === 'string' && description.includes('<');
  const safeHtml = isHtml ? sanitizeHtml(description) : '';
  const defaultTagStyle = { backgroundColor: '#931b1d20', color: '#931b1d' };
  const tagClass = 'text-xs px-2 py-1 rounded-full font-medium';

  return (
    <div className={className}>
      <div className="text-gray-600 text-sm mb-2">
        {isHtml && safeHtml ? (
          <div className="prose prose-sm max-w-none text-gray-600" dangerouslySetInnerHTML={{ __html: safeHtml }} />
        ) : (
          <p>{description || 'Bez opisa'}</p>
        )}
      </div>
      {materialLink && (
        <a
          href={materialLink.startsWith('http') ? materialLink : `https://${materialLink}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-medium text-[#931b1d] hover:underline mb-2"
        >
          Dodatni materijali
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
        </a>
      )}
      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span key={tag} className={tagClass} style={tagStyle || defaultTagStyle}>{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper function to validate and format three-field name
const validateAndFormatThreeFieldName = (name, surname1, surname2) => {
  const errors = [];
  
  if (!name || !name.trim()) {
    errors.push('Ime je obavezno');
  }
  
  // Surnames are optional but must be non-empty if provided
  if (surname1 && !surname1.trim()) {
    errors.push('Prezime 1 ne može biti prazno ako je uneto');
  }
  if (surname2 && !surname2.trim()) {
    errors.push('Prezime 2 ne može biti prazno ako je uneto');
  }
  
  if (errors.length > 0) {
    return { isValid: false, errors };
  }
  
  return {
    isValid: true,
    name: capitalize(name.trim()),
    surname1: surname1 ? capitalize(surname1.trim()) : '',
    surname2: surname2 ? capitalize(surname2.trim()) : ''
  };
};

export default function AcademyLeaderboard() {
  const [currentUser, setCurrentUser] = useState(null);
  const [members, setMembers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initializingData, setInitializingData] = useState(false);

  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    try {
      // console.log('🚀 Starting app initialization...');
      setLoading(true);
      setError(null);

      // Load settings first
      await loadSettings();
      
      // Load members
      await loadMembers();
      
      // console.log('✅ App initialization complete');
      setLoading(false);
    } catch (err) {
      console.error('❌ Error initializing app:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      // console.log('📋 Loading settings...');
      const settingsDoc = await getDoc(doc(db, 'settings', 'main'));
      
      if (settingsDoc.exists()) {
        const settingsData = settingsDoc.data();
        setSettings({ ...settingsData, currentQuarter: getEffectiveQuarter(settingsData) });
      } else {
        // console.log('⚠️ No settings found, creating defaults...');
        const defaultSettings = {
          sharedPasscode: 'Academy2025',
          currentQuarter: getCurrentQuarter(),
          pointsConfig: {
            challenge: 10,
            groupCall: 20,
            groupActivity: 20,
            workshop: 20,
            focusSession: 10
          },
          maxDailyFocusPoints: 30,
          adminEmail: 'jelena@example.com',
          adminPassword: ''
        };
        await setDoc(doc(db, 'settings', 'main'), defaultSettings);
        setSettings({ ...defaultSettings, currentQuarter: getEffectiveQuarter(defaultSettings) });
      }
    } catch (error) {
      console.error('❌ Error loading settings:', error);
      throw error;
    }
  };

  const loadMembers = async () => {
    try {
      // console.log('👥 Loading members...');
      const membersSnapshot = await getDocs(collection(db, 'members'));
      // console.log(`📊 Found ${membersSnapshot.size} members in database`);
      
      const membersList = membersSnapshot.docs.map(doc => {
        const data = doc.data();
        // console.log('Member:', doc.id, data);
        return {
          id: doc.id,
          ...data
        };
      });
      
      // console.log('✅ Members loaded:', membersList);
      setMembers(membersList);
      
      if (membersList.length === 0) {
        // console.log('⚠️ No members found in database!');
      }
    } catch (error) {
      console.error('❌ Error loading members:', error);
      throw error;
    }
  };

  const initializeSampleData = async () => {
    try {
      setInitializingData(true);
      // console.log('🎯 Initializing sample data...');

      // Create sample members
      const sampleMembers = [
        {
          name: 'Jelena Popović',
          email: 'jelena.popovic@example.com',
          isAdmin: true,
          avatarUrl: '',
          joinedDate: new Date().toISOString()
        },
        {
          name: 'Marko Marković',
          email: 'marko.markovic@example.com',
          isAdmin: false,
          avatarUrl: '',
          joinedDate: new Date().toISOString()
        },
        {
          name: 'Ana Jovanović',
          email: 'ana.jovanovic@example.com',
          isAdmin: false,
          avatarUrl: '',
          joinedDate: new Date().toISOString()
        }
      ];

      for (const member of sampleMembers) {
        await addDoc(collection(db, 'members'), member);
        // console.log(`✅ Created member: ${member.name}`);
      }

      // Create sample challenges
      const sampleChallenges = [
        {
          title: 'Napiši 5 blog postova',
          description: 'Kreiraj 5 SEO optimizovanih blog postova za svoj biznis',
          points: 100,
          isActive: true,
          isRecurring: false,
          requiresSubmission: false,
          deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date().toISOString()
        },
        {
          title: 'Dizajniraj email kampanju',
          description: 'Kreiraj i pošalji email kampanju svojoj listi',
          points: 75,
          isActive: true,
          isRecurring: false,
          requiresSubmission: true,
          deadline: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date().toISOString()
        },
        {
          title: 'Nedeljni check-in',
          description: 'Podeli svoj nedeljni progres sa grupom',
          points: 20,
          isActive: true,
          isRecurring: true,
          requiresSubmission: false,
          createdAt: new Date().toISOString()
        }
      ];

      for (const challenge of sampleChallenges) {
        await addDoc(collection(db, 'challenges'), challenge);
        // console.log(`✅ Created challenge: ${challenge.title}`);
      }

      // console.log('✅ Sample data initialization complete!');
      // console.log('Uzorni podaci su uspešno kreirani! Osveži stranicu.');
      setInitializingData(false);
      
      // Reload data
      await initializeApp();
    } catch (error) {
      console.error('❌ Error initializing sample data:', error);
      // console.log('Greška prilikom kreiranja uzornih podataka: ' + error.message);
      setInitializingData(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl text-gray-600 mb-4">Učitavanje...</div>
          <div className="text-sm text-gray-500">Povezivanje sa Firebase...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="text-xl font-bold mb-4" style={{color: '#931b1d'}}>❌ Greška</div>
          <p className="text-gray-700 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full text-white py-2 rounded-lg transition"
            style={{backgroundColor: '#931b1d'}}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#7a1618'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#931b1d'}
          >
            Pokušaj ponovo
          </button>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <LoginView 
        members={members} 
        settings={settings} 
        onLogin={setCurrentUser}
        onInitializeSampleData={initializeSampleData}
        initializingData={initializingData}
      />
    );
  }

  return (
    <MainLayout 
      user={currentUser} 
      settings={settings}
      onLogout={() => setCurrentUser(null)}
      onRefreshSettings={loadSettings}
      onRefreshMembers={loadMembers}
    />
  );
}

function LoginView({ members, settings, onLogin, onInitializeSampleData, initializingData }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAdminLoading(true);
    setAdminError('');
    try {
      const inputEmail = (adminEmail || '').toLowerCase().trim();
      const inputPassword = (adminPassword || '').trim();
      if (!inputEmail || !inputPassword) {
        setAdminError('Unesite email i lozinku.');
        setAdminLoading(false);
        return;
      }
      const storedAdminEmail = (settings?.adminEmail || '').toLowerCase().trim();
      const storedAdminPassword = (settings?.adminPassword || '').trim();
      if (!storedAdminPassword) {
        setAdminError('Admin lozinka nije podešena. Podesite polje adminPassword u Firebase (settings/main).');
        setAdminLoading(false);
        return;
      }
      if (inputEmail !== storedAdminEmail || inputPassword !== storedAdminPassword) {
        setAdminError('Pogrešan email ili lozinka.');
        setAdminLoading(false);
        return;
      }
      const foundMember = members.find(m => (m.email || '').toLowerCase().trim() === inputEmail);
      if (!foundMember) {
        setAdminError('Nema člana sa ovim email-om. Dodajte se u članove kao admin.');
        setAdminLoading(false);
        return;
      }
      if (!foundMember.isAdmin) {
        setAdminError('Ovaj nalog nema admin privilegije. Označite člana kao admin u bazi.');
        setAdminLoading(false);
        return;
      }
      onLogin(foundMember);
    } catch (err) {
      setAdminError('Greška prilikom prijave. Pokušajte ponovo.');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const inputEmail = (email || '').toLowerCase().trim();
      if (!inputEmail) {
        setError('Unesite email adresu.');
        setLoading(false);
        return;
      }

      // Check if this is the admin email - restrict admin from using regular login
      const adminEmail = (settings?.adminEmail || '').toLowerCase().trim();
      if (adminEmail && inputEmail === adminEmail) {
        setError('Admin nalog zahteva lozinku. Koristite link "Admin" ispod za admin prijavu.');
        setLoading(false);
        return;
      }

      // Find member by email only — email is the sole identifier; name/surname stay in DB for display
      const foundMember = members.find(member => {
        const memberEmail = (member.email || '').toLowerCase().trim();
        return memberEmail === inputEmail;
      });

      if (foundMember) {
        onLogin(foundMember);
      } else {
        setError('Nije pronađen član sa ovom email adresom. Proverite unos ili se obratite administratoru.');
      }
    } catch (err) {
      setError('Greška prilikom prijave. Pokušajte ponovo.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    if (error) setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 lg:p-8 w-full max-w-lg">
        {showAdminLogin ? (
          <>
            <div className="text-center mb-6">
              <h1 className="text-xl font-semibold text-gray-800">Admin prijava</h1>
              <p className="text-gray-500 text-sm mt-1">Email i lozinka za pristup admin panelu</p>
            </div>
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => { setAdminEmail(e.target.value); setAdminError(''); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent text-sm"
                  placeholder="admin@example.com"
                  required
                  disabled={adminLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lozinka</label>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => { setAdminPassword(e.target.value); setAdminError(''); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent text-sm"
                  placeholder="••••••••"
                  required
                  disabled={adminLoading}
                />
              </div>
              {adminError && (
                <div className="bg-[#931b1d]/10 border border-[#931b1d]/20 rounded-lg p-3">
                  <p className="text-[#931b1d] text-sm">{adminError}</p>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setShowAdminLogin(false); setAdminError(''); setAdminEmail(''); setAdminPassword(''); }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                >
                  Nazad
                </button>
                <button
                  type="submit"
                  disabled={adminLoading}
                  className="flex-1 text-white py-2 rounded-lg transition font-medium disabled:opacity-50 text-sm"
                  style={{ backgroundColor: '#931b1d' }}
                >
                  {adminLoading ? 'Prijavljivanje...' : 'Prijavi se'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <div className="text-center mb-6 lg:mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">#lansiramozajedno</h1>
              <p className="text-gray-600 text-sm sm:text-base">Hej, dobro došla u zonu fokusa! Ovo je tvoj način da ostaneš dosledna i slaviš svoje mikro-pobede.</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-3 lg:space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent text-sm sm:text-base"
                  placeholder="ana.markovic@example.com"
                  required
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Unesite email adresu koju koristite u zajednici — prijava je samo putem emaila.
                </p>
              </div>

              {error && (
                <div className="bg-[#931b1d]/10 border border-[#931b1d]/20 rounded-lg p-4">
                  <p className="text-[#931b1d] text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full text-white py-2 sm:py-3 rounded-lg transition font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                style={{ backgroundColor: '#931b1d' }}
              >
                {loading ? 'Prijavljivanje...' : 'Prijavi se'}
              </button>
            </form>

            {members.length === 0 && (
              <div className="mt-4 sm:mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-center">
                <p className="text-yellow-800 font-medium mb-2 text-sm sm:text-base">Nema članova u sistemu</p>
                <p className="text-yellow-700 text-xs sm:text-sm mb-3">
                  Admin mora prvo da doda članove kroz admin panel.
                </p>
                <button
                  onClick={onInitializeSampleData}
                  disabled={initializingData}
                  className="bg-[#5c614d] text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-[#5c614d] transition text-xs sm:text-sm disabled:opacity-50"
                >
                  {initializingData ? 'Kreiranje...' : '✨ Kreiraj uzorne podatke'}
                </button>
              </div>
            )}

            <div className="mt-4 sm:mt-6 text-center">
              <p className="text-xs text-gray-500">
                Unesi email adresu da se prijaviš. Ime i prezime se automatski učitavaju iz baze — prijava je samo putem emaila.
              </p>
              <button
                type="button"
                onClick={() => setShowAdminLogin(true)}
                className="mt-2 text-xs text-gray-400 hover:text-gray-600 underline focus:outline-none"
              >
                Admin
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MainLayout({ user, settings, onLogout, onRefreshSettings, onRefreshMembers }) {
  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <div className="flex h-screen" style={{ backgroundColor: '#f7f3ef' }}>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 text-white flex flex-col transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`} style={{ backgroundColor: '#323433' }}>
        <div className="p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-lg lg:text-xl font-bold">#lansiramozajedno</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-300 hover:text-white"
            >
              ✕
            </button>
          </div>
        </div>

        {/* User Profile */}
        <div className="px-3 lg:px-4 pb-3 lg:pb-4 border-b" style={{ borderColor: '#5c614d' }}>
          <div className="flex flex-col items-center">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt={user.name || 'User'} className="w-12 h-12 lg:w-16 lg:h-16 rounded-full object-cover mb-1 lg:mb-2 border-2" style={{ borderColor: '#931b1d' }} />
            ) : (
              <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-full flex items-center justify-center text-white text-sm lg:text-lg font-bold mb-1 lg:mb-2" style={{ backgroundColor: '#931b1d' }}>
                {getInitials(user.name)}
              </div>
            )}
            <h2 className="font-semibold text-xs lg:text-sm text-center">{user.name || 'Korisnik'}</h2>
            <p className="text-xs text-center hidden lg:block" style={{ color: '#8a9a8a' }}>{user.email || 'Bez emaila'}</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-1 lg:p-2">
          {/* Dashboard - First Menu Item */}
          <button
            onClick={() => {
              setActiveView('dashboard');
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg transition mb-0.5 lg:mb-1 text-sm lg:text-base ${
              activeView === 'dashboard' ? 'text-white' : 'text-gray-300 hover:bg-gray-800'
            }`}
            style={activeView === 'dashboard' ? { backgroundColor: '#931b1d' } : {}}
          >
            <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            Moj pregled
          </button>

          <button
            onClick={() => {
              setActiveView('challenges');
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg transition mb-0.5 lg:mb-1 text-sm lg:text-base ${
              activeView === 'challenges' ? 'text-white' : 'text-gray-300 hover:bg-gray-800'
            }`}
            style={activeView === 'challenges' ? { backgroundColor: '#931b1d' } : {}}
          >
            <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Izaberi mikro-akcije
          </button>

          <button
            onClick={() => {
              setActiveView('focus');
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg transition mb-0.5 lg:mb-1 text-sm lg:text-base ${
              activeView === 'focus' ? 'text-white' : 'text-gray-300 hover:bg-gray-800'
            }`}
            style={activeView === 'focus' ? { backgroundColor: '#931b1d' } : {}}
          >
            <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Fokus zona
          </button>

          <button
            onClick={() => {
              setActiveView('events');
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg transition mb-0.5 lg:mb-1 text-sm lg:text-base ${
              activeView === 'events' ? 'text-white' : 'text-gray-300 hover:bg-gray-800'
            }`}
            style={activeView === 'events' ? { backgroundColor: '#931b1d' } : {}}
          >
            <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Mesečni događaji
          </button>

          <button
            onClick={() => {
              setActiveView('leaderboard');
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg transition mb-0.5 lg:mb-1 text-sm lg:text-base ${
              activeView === 'leaderboard' ? 'text-white' : 'text-gray-300 hover:bg-gray-800'
            }`}
            style={activeView === 'leaderboard' ? { backgroundColor: '#931b1d' } : {}}
          >
            <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Rang Lista
          </button>

          <button
            onClick={() => {
              setActiveView('victories');
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg transition mb-0.5 lg:mb-1 text-sm lg:text-base ${
              activeView === 'victories' ? 'text-white' : 'text-gray-300 hover:bg-gray-800'
            }`}
            style={activeView === 'victories' ? { backgroundColor: '#931b1d' } : {}}
          >
            <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Naše male pobede
          </button>

          {/* User Profile/Admin Buttons */}
          {!user.isAdmin && (
            <button
              onClick={() => {
                setActiveView('profile');
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg transition mb-0.5 lg:mb-1 text-sm lg:text-base ${
                activeView === 'profile' ? 'text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
              style={activeView === 'profile' ? { backgroundColor: '#5c614d' } : {}}
            >
              <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Moj profil
            </button>
          )}

          {user.isAdmin && (
            <button
              onClick={() => {
                setActiveView('admin');
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg transition mb-0.5 lg:mb-1 text-sm lg:text-base ${
                activeView === 'admin' ? 'text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
              style={activeView === 'admin' ? { backgroundColor: '#931b1d' } : {}}
            >
              <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Admin Panel
            </button>
          )}

          {/* Logout Button */}
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 lg:py-2 text-gray-300 hover:bg-gray-800 rounded-lg transition text-sm lg:text-base mt-2"
          >
            <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Odjavi se
          </button>
        </nav>

      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {/* Mobile Header */}
        <div className="lg:hidden shadow-sm px-4 py-3 flex items-center justify-between" style={{ backgroundColor: '#f7f3ef', borderBottom: '1px solid #5c614d' }}>
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-600 hover:text-gray-900"
          >
            ☰
          </button>
          <h1 className="text-lg font-semibold text-gray-800">#lansiramozajedno</h1>
          <div className="w-8"></div> {/* Spacer for centering */}
        </div>

        <div className="p-4 lg:p-6">
        {activeView === 'dashboard' && <DashboardView user={user} settings={settings} />}
        {activeView === 'challenges' && <ChallengesView user={user} settings={settings} />}
          {activeView === 'focus' && <FocusZonaView user={user} settings={settings} />}
          {activeView === 'events' && <DogađajiView user={user} settings={settings} />}
        {activeView === 'leaderboard' && <LeaderboardView user={user} settings={settings} />}
          {activeView === 'victories' && <VictoriesView user={user} settings={settings} />}
          {activeView === 'profile' && <ProfileView user={user} settings={settings} db={db} storage={storage} onRefresh={onRefreshMembers} />}
        {activeView === 'admin' && user.isAdmin && (
          <AdminView 
            user={user} 
            settings={settings} 
              db={db}
              storage={storage}
            onRefreshSettings={onRefreshSettings}
            onRefreshMembers={onRefreshMembers}
          />
        )}
        </div>
      </main>
    </div>
  );
}

function DashboardView({ user, settings }) {
  const [userPoints, setUserPoints] = useState(0);
  const [userRank, setUserRank] = useState(0);
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVictoryModal, setShowVictoryModal] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [victoryText, setVictoryText] = useState('');
  const [victoryLink, setVictoryLink] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [levelFocus, setLevelFocus] = useState(null);
  const [totalActionsCount, setTotalActionsCount] = useState(0);

  // Common emojis for victory posts
  const commonEmojis = [
    '😀', '😃', '😊', '😍', '🤩', '🥳',
    '👍', '👏', '💪', '🔥', '⭐', '🎉',
    '🎯', '💯', '🏆', '🎊', '✨', '❤️',
    '🙌', '😄', '😁', '😎', '🤗', '💡'
  ];

  useEffect(() => {
    loadDashboardData();
  }, [user, settings]);

  const loadDashboardData = async () => {
    try {
      const quarter = getEffectiveQuarter(settings);
      await ensureQuarterExists(db, quarter);
      const pointsDoc = await getDoc(
        doc(db, 'quarters', quarter, 'points', user.id)
      );
      if (pointsDoc.exists()) {
        setUserPoints(pointsDoc.data().totalPoints || 0);
      }

      const allPointsSnapshot = await getDocs(
        query(
          collection(db, 'quarters', quarter, 'points'),
          orderBy('totalPoints', 'desc')
        )
      );
      const rankIndex = allPointsSnapshot.docs.findIndex(doc => doc.id === user.id);
      setUserRank(rankIndex >= 0 ? rankIndex + 1 : 0);

      const allActivitiesSnapshot = await getDocs(
        query(
          collection(db, 'quarters', quarter, 'activities'),
          where('memberId', '==', user.id)
        )
      );
      setTotalActionsCount(allActivitiesSnapshot.size);

      const acceptedSnapshot = await getDocs(
        query(
          collection(db, 'quarters', quarter, 'acceptedChallenges'),
          where('memberId', '==', user.id)
        )
      );
      const acceptedChallenges = acceptedSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const userActivitiesSnapshot = await getDocs(
        query(
          collection(db, 'quarters', quarter, 'activities'),
          where('memberId', '==', user.id),
          where('type', '==', 'challenge')
        )
      );
      const completedChallengeIds = userActivitiesSnapshot.docs.map(doc => doc.data().challengeId);
      
      // Filter accepted challenges to show only non-completed ones
      const activeChallengesFiltered = acceptedChallenges.filter(c => !completedChallengeIds.includes(c.challengeId));
      
      // Get completed challenges with victory text
      const completedActivities = userActivitiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get victory posts for completed challenges
      const victoriesSnapshot = await getDocs(
        query(
          collection(db, 'victories'),
          where('memberId', '==', user.id)
        )
      );
      const victories = victoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Combine completed activities with victory text and link
      const completedWithVictories = completedActivities.map(activity => {
        const victory = victories.find(v => v.challengeId === activity.challengeId);
        return {
          ...activity,
          victoryText: victory ? victory.victoryText : null,
          victoryLink: victory ? victory.victoryLink : null,
          victoryTimestamp: victory ? victory.timestamp : null
        };
      }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setActiveChallenges(activeChallengesFiltered);
      setCompletedChallenges(completedWithVictories);

      // Level recommendations (nivo fokus)
      const levelKey = user.nivo || 'Osnovni';
      try {
        const nivoSnap = await getDoc(doc(db, 'nivoFokus', levelKey));
        if (nivoSnap.exists() && nivoSnap.data()) {
          setLevelFocus(nivoSnap.data());
        } else {
          setLevelFocus(null);
        }
      } catch (_) {
        setLevelFocus(null);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleCompleteChallenge = async (challenge) => {
    try {
      const quarter = getEffectiveQuarter(settings);
      await ensureQuarterExists(db, quarter);
      await addDoc(collection(db, 'quarters', quarter, 'activities'), {
        memberId: user.id,
        type: 'challenge',
        challengeId: challenge.challengeId || challenge.id,
        challengeTitle: challenge.challengeTitle || challenge.title,
        challengeDescription: challenge.challengeDescription,
        challengeMaterialLink: challenge.challengeMaterialLink || '',
        challengeTags: challenge.challengeTags || [],
        points: challenge.challengePoints || challenge.points,
        timestamp: new Date().toISOString()
      });

      const pointsRef = doc(db, 'quarters', quarter, 'points', user.id);
      const pointsDoc = await getDoc(pointsRef);
      
      const pointsToAdd = challenge.challengePoints ?? challenge.points ?? 0;
      if (pointsDoc.exists()) {
        await updateDoc(pointsRef, {
          totalPoints: (pointsDoc.data().totalPoints || 0) + pointsToAdd,
          lastUpdated: new Date().toISOString()
        });
      } else {
        await setDoc(pointsRef, {
          memberId: user.id,
          totalPoints: pointsToAdd,
          lastUpdated: new Date().toISOString()
        });
      }

      // console.log(`Izazov završen! +${pointsToAdd} poena`);
      loadDashboardData();
    } catch (error) {
      console.error('Error completing challenge:', error);
      // console.log('Greška prilikom završavanja izazova.');
    }
  };

  const getFirstName = (fullName) => {
    if (!fullName) return 'korisnče';
    return fullName.split(' ')[0];
  };

  const getGender = (name) => {
    if (!name) return 'došao';
    const lowerName = name.toLowerCase();
    if (lowerName.includes('jelena') || lowerName.includes('ana') || lowerName.includes('marija') || 
        lowerName.includes('milica') || lowerName.includes('sara') || lowerName.includes('ivana')) {
      return 'došla';
    }
    return 'došao';
  };

  const quarterNum = Math.ceil((new Date().getMonth() + 1) / 3);
  const quarterLabel = `Q${quarterNum}`;

  if (loading) return <div className="p-8">Učitavanje...</div>;

  return (
    <div className="p-4 lg:p-8">
      <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-2">
        Dobro {getGender(user.name)} u {(user.nivo || 'Osnovni').toLowerCase()} nivo, {getFirstName(user.name)}!
      </h1>
      <p className="text-gray-600 mb-6 lg:mb-8 text-sm lg:text-base">Ovde ćeš pronaći sve najbitnije informacije, događaje i predlog aktivnosti koje odgovaraju tvom nivou.</p>

      {/* Three Main Sections */}
      <div className="space-y-8">
        
        {/* Section 1: Tvoj status u ovom kvartalu */}
        <div className="bg-gradient-to-r from-[#931b1d]/5 to-[#931b1d]/10 rounded-2xl p-6 lg:p-8">
          <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
            <div className="bg-[#931b1d]/20 p-2 rounded-lg">
              <svg className="w-5 h-5 text-[#931b1d]" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
            Tvoj status u ovom kvartalu - {quarterLabel}
          </h2>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            <div>
              <p className="text-xs lg:text-sm text-gray-600 mb-2">Poeni</p>
              <div className="bg-white rounded-full shadow-sm px-4 py-3 text-center">
                <span className="text-[0.9375rem] lg:text-[1.125rem] font-bold text-gray-800">{userPoints}</span>
              </div>
            </div>
            <div>
              <p className="text-xs lg:text-sm text-gray-600 mb-2">Rang</p>
              <div className="bg-white rounded-full shadow-sm px-4 py-3 text-center">
                <span className="text-[0.9375rem] lg:text-[1.125rem] font-bold text-gray-800">#{userRank || '-'}</span>
              </div>
            </div>
            <div>
              <p className="text-xs lg:text-sm text-gray-600 mb-2">Nivo</p>
              <div className="bg-white rounded-full shadow-sm px-4 py-3 text-center">
                <span className="text-[0.9375rem] lg:text-[1.125rem] font-bold text-gray-800">{user.nivo || 'Osnovni'}</span>
              </div>
            </div>
            <div>
              <p className="text-xs lg:text-sm text-gray-600 mb-2">Ukupno akcija</p>
              <div className="bg-white rounded-full shadow-sm px-4 py-3 text-center">
                <span className="text-[0.9375rem] lg:text-[1.125rem] font-bold text-gray-800">{totalActionsCount}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Predlog mikro-akcija - level recommendations from nivoFokus (by user level and date) */}
        {levelFocus && (levelFocus.leftColumn?.items?.length > 0 || levelFocus.rightColumn?.items?.length > 0) ? (
          <div className="rounded-2xl p-6 lg:p-8" style={{ backgroundColor: '#f5f0e8' }}>
            <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-6">Predlog mikro-akcija</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-4">Moduli i zadaci</h3>
                <div className="space-y-3">
                  {(levelFocus.leftColumn.items || []).filter((i) => i && (i.title || '').trim()).map((item, idx) => {
                    const href = item.url && (item.url.startsWith('http') ? item.url : `https://${item.url}`);
                    return (
                      <div key={`left-${idx}`} className="flex gap-2 items-center">
                        <span className="flex-1 bg-white rounded-lg px-4 py-3 text-gray-800 text-sm truncate">{item.title}</span>
                        {href && (
                          <a href={href} target="_blank" rel="noopener noreferrer" className="bg-gray-900 text-white text-sm font-medium px-4 py-3 rounded-lg whitespace-nowrap hover:bg-gray-800">
                            PRISTUPI
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
                {levelFocus.leftColumn.bottomLinkUrl && (
                  <p className="mt-4 text-sm text-gray-700">
                    <a href={levelFocus.leftColumn.bottomLinkUrl.startsWith('http') ? levelFocus.leftColumn.bottomLinkUrl : `https://${levelFocus.leftColumn.bottomLinkUrl}`} target="_blank" rel="noopener noreferrer" className="underline text-gray-800 font-medium">Ovde</a> zakaži mesečnu konsultaciju.
                  </p>
                )}
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-4">Važni događaji za tvoj nivo</h3>
                <div className="space-y-3">
                  {(levelFocus.rightColumn.items || []).filter((i) => i && (i.title || '').trim()).map((item, idx) => {
                    const href = item.url && (item.url.startsWith('http') ? item.url : `https://${item.url}`);
                    return (
                      <div key={`right-${idx}`} className="flex gap-2 items-center">
                        <span className="flex-1 bg-white rounded-lg px-4 py-3 text-gray-800 text-sm truncate">{item.title}</span>
                        {href && (
                          <a href={href} target="_blank" rel="noopener noreferrer" className="bg-gray-900 text-white text-sm font-medium px-4 py-3 rounded-lg whitespace-nowrap hover:bg-gray-800">
                            PRIKLJUČI SE
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
                {levelFocus.rightColumn.bottomLinkUrl && (
                  <p className="mt-4 text-sm text-gray-700 flex items-center gap-2">
                    <a href={levelFocus.rightColumn.bottomLinkUrl.startsWith('http') ? levelFocus.rightColumn.bottomLinkUrl : `https://${levelFocus.rightColumn.bottomLinkUrl}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-gray-800">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9a1 1 0 001.804.98l10-2A1 1 0 0018 13V3z" /></svg>
                      Poslušaj Jelenin savet za tvoj biznis.
                    </a>
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl p-6 lg:p-8 border border-gray-200 bg-gray-50/50">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2">Predlog mikro-akcija (nivo)</h2>
            <p className="text-gray-500 text-sm lg:text-base">
              Preporuke za tvoj nivo (<strong>{user.nivo || 'Osnovni'}</strong>) dodaje administrator u delu <strong>Admin → Nivo fokus</strong>. Ako ne vidiš module i događaje, obrati se administratoru ili proveri ponovo kasnije.
            </p>
          </div>
        )}

        {/* Section 2: My Focus - Active Challenges (accepted for current quarter) */}
      <div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#931b1d20' }}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#931b1d' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            Predlog mikro-akcija
          </h2>
          
        <div className="space-y-3 lg:space-y-4">
          {activeChallenges.length === 0 ? (
              <div className="bg-white rounded-xl shadow p-6 lg:p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm lg:text-base">Nema aktivnih mikro-akcija u ovom trenutku</p>
                <p className="text-gray-400 text-xs lg:text-sm mt-2">Otvori <strong>Mikro-akcije</strong> u meniju i prihvati izazove koje želiš da završiš—pojaviće se ovde kao tvoj fokus za trenutni kvartal.</p>
            </div>
          ) : (
            activeChallenges.map(challenge => (
                <div key={challenge.id} className="bg-white rounded-xl shadow p-4 lg:p-6 hover:shadow-lg transition border-l-4" style={{ borderLeftColor: '#931b1d' }}>
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                  <div className="flex-1">
                      <h3 className="text-base lg:text-lg font-semibold text-gray-800 mb-2">{challenge.challengeTitle || 'Bez naslova'}</h3>
                      <MicroActionBody
                        description={challenge.challengeDescription}
                        materialLink={challenge.challengeMaterialLink}
                        tags={challenge.challengeTags}
                        className="mb-3 lg:mb-4"
                      />
                      {challenge.challengeDeadline && (
                        <p className="text-xs lg:text-sm text-gray-500 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Rok: {new Date(challenge.challengeDeadline).toLocaleDateString('sr-RS')}
                      </p>
                    )}
                  </div>
                  <div className="text-center lg:text-right lg:ml-4">
                      <div className="text-xl lg:text-2xl font-bold text-[#931b1d] mb-2">{challenge.challengePoints || 0} poena</div>
                      
                    <button
                      onClick={() => {
                        setSelectedChallenge(challenge);
                        setShowVictoryModal(true);
                      }}
                        className="bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d]/90 transition text-sm font-medium w-full sm:w-auto"
                    >
                      Završila
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

        {/* Section 3: My Done Challenges */}
        <div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#5c614d20' }}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#5c614d' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            Moje male pobede
          </h2>
          
          <div className="space-y-3 lg:space-y-4">
            {completedChallenges.length === 0 ? (
              <div className="bg-white rounded-xl shadow p-6 lg:p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm lg:text-base">Još niste završili nijednu mikro-akciju</p>
                <p className="text-gray-400 text-xs lg:text-sm mt-2">Završite mikro-akcije iz sekcije "Predlog mikro-akcija" da ih vidite ovde</p>
              </div>
            ) : (
              completedChallenges.map(challenge => (
                <div key={challenge.id} className="bg-white rounded-xl shadow p-4 lg:p-6 hover:shadow-lg transition border-l-4" style={{ borderLeftColor: '#5c614d' }}>
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-base lg:text-lg font-semibold text-gray-800">{challenge.challengeTitle || 'Bez naslova'}</h3>
                        <div className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: '#5c614d20', color: '#5c614d' }}>
                          Završeno
                        </div>
                      </div>
                      <MicroActionBody
                        description={challenge.challengeDescription}
                        materialLink={challenge.challengeMaterialLink}
                        tags={challenge.challengeTags}
                        className="mb-3"
                        tagStyle={{ backgroundColor: '#5c614d20', color: '#5c614d' }}
                      />
                      <div className="text-sm text-gray-500 mb-3">
                        Završeno: {new Date(challenge.timestamp).toLocaleDateString('sr-RS')}
                      </div>
                      {challenge.victoryText ? (
                        <div className="border rounded-lg p-3 lg:p-4" style={{ backgroundColor: '#5c614d10', borderColor: '#5c614d30' }}>
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#5c614d' }}>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                            </svg>
                            <span className="text-sm font-medium" style={{ color: '#5c614d' }}>Vaš komentar:</span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{challenge.victoryText}</p>
                          {challenge.victoryLink && (
                            <a
                              href={challenge.victoryLink.startsWith('http') ? challenge.victoryLink : `https://${challenge.victoryLink}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm font-medium mt-2"
                              style={{ color: '#5c614d' }}
                            >
                              Link
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                            </a>
                          )}
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 lg:p-4">
                          <p className="text-sm text-gray-500 italic">Nema komentara za ovu mikro-akciju</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="text-center lg:text-right lg:ml-4">
                      <div className="text-xl lg:text-2xl font-bold mb-2" style={{ color: '#5c614d' }}>+{challenge.points || 0} poena</div>
                      <div className="text-xs text-gray-500">
                        {challenge.victoryTimestamp ? 
                          `Komentar: ${new Date(challenge.victoryTimestamp).toLocaleDateString('sr-RS')}` : 
                          'Bez komentara'
                        }
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Victory Modal */}
      {showVictoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 relative">
            <button type="button" onClick={() => { setShowVictoryModal(false); setVictoryText(''); setVictoryLink(''); setSelectedChallenge(null); }} className="absolute top-4 right-4 p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition" aria-label="Zatvori">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 pr-8">Podeli pobedu, utisak ili zapažanje koje može da koristi zajednici.</h2>
            
            {/* Emoji Button Above Textarea */}
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="text-2xl hover:scale-110 transition cursor-pointer"
              >
                😊
              </button>
            </div>
            
            <div className="relative">
              <textarea
                value={victoryText}
                onChange={(e) => setVictoryText(e.target.value)}
                placeholder="Koja je tvoja pobeda? Šta si uradila i naučila iz ove mikro-akcije, a da bi značilo ostalima u zajednici?"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent resize-none mb-4"
                rows={6}
              />
              
              {/* Emoji Picker */}
              {showEmojiPicker && (
                <div className="absolute right-0 top-0 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-10" style={{ maxWidth: '250px' }}>
                  <div className="grid grid-cols-6 gap-2">
                    {commonEmojis.map((emoji, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          setVictoryText(victoryText + emoji);
                          setShowEmojiPicker(false);
                        }}
                        className="text-2xl hover:scale-125 transition cursor-pointer p-1 hover:bg-gray-100 rounded"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Link</label>
              <p className="text-xs text-gray-500 mb-2">Ubaci link do dokumenta u kom si pisala</p>
              <input
                type="url"
                value={victoryLink}
                onChange={(e) => setVictoryLink(e.target.value.trim())}
                placeholder="https://..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowVictoryModal(false);
                  setVictoryText('');
                  setVictoryLink('');
                  setSelectedChallenge(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Zatvori
              </button>
              <button
                onClick={async () => {
                  if (!victoryText.trim() || !selectedChallenge) {
                    // console.log('Molimo unesite tekst pobede');
                    return;
                  }

                  try {
                    const quarter = getEffectiveQuarter(settings);
                    await ensureQuarterExists(db, quarter);
                    await addDoc(collection(db, 'quarters', quarter, 'activities'), {
                      memberId: user.id,
                      type: 'challenge',
                      challengeId: selectedChallenge.challengeId,
                      challengeTitle: selectedChallenge.challengeTitle,
                      challengeDescription: selectedChallenge.challengeDescription,
                      challengeMaterialLink: selectedChallenge.challengeMaterialLink || '',
                      challengeTags: selectedChallenge.challengeTags || [],
                      points: selectedChallenge.challengePoints,
                      proofUrl: null,
                      timestamp: new Date().toISOString()
                    });

                    // Add victory post to database
                    await addDoc(collection(db, 'victories'), {
                      memberId: user.id,
                      memberName: user.name,
                      challengeId: selectedChallenge.challengeId,
                      challengeTitle: selectedChallenge.challengeTitle,
                      victoryText: victoryText.trim(),
                      victoryLink: victoryLink.trim() || null,
                      timestamp: new Date().toISOString()
                    });

                    // Update points
                    const pointsRef = doc(db, 'quarters', quarter, 'points', user.id);
                    const pointsDoc = await getDoc(pointsRef);
                    
                    if (pointsDoc.exists()) {
                      await updateDoc(pointsRef, {
                        totalPoints: (pointsDoc.data().totalPoints || 0) + (selectedChallenge.challengePoints || 0),
                        lastUpdated: new Date().toISOString()
                      });
                    } else {
                      await setDoc(pointsRef, {
                        memberId: user.id,
                        totalPoints: selectedChallenge.challengePoints || 0,
                        lastUpdated: new Date().toISOString()
                      });
                    }

                    // Show confetti animation
                    setShowConfetti(true);
                    
                    // Close modal and reload after animation
                    setTimeout(() => {
                      setShowVictoryModal(false);
                      setShowConfetti(false);
                      setVictoryText('');
                      setVictoryLink('');
                      setSelectedChallenge(null);
                      loadDashboardData();
                    }, 5000);
                  } catch (error) {
                    console.error('Error completing challenge:', error);
                    // console.log('Greška prilikom završavanja izazova');
                  }
                }}
                className="flex-1 px-4 py-2 bg-[#931b1d] text-white rounded-lg hover:bg-[#931b1d]/90 transition"
              >
                Podeli pobedu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Epic Celebration Animation */}
      {showConfetti && (
        <div className="fixed inset-0 z-[60] pointer-events-none overflow-hidden">
          {/* Simple Confetti */}
          {[...Array(20)].map((_, i) => (
            <div
              key={`confetti-${i}`}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${4 + Math.random() * 2}s`,
              }}
            >
              <div
                className="rounded-full"
                style={{
                  width: `${4 + Math.random() * 6}px`,
                  height: `${4 + Math.random() * 6}px`,
                  backgroundColor: ['#931b1d', '#931b1d80', '#931b1d60', '#931b1d40'][Math.floor(Math.random() * 4)],
                }}
              />
            </div>
          ))}
          
          {/* Victory message overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center bg-white rounded-2xl p-8 shadow-2xl border-2" style={{ borderColor: '#931b1d' }}>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#931b1d20' }}>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#931b1d' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-3" style={{ color: '#931b1d' }}>Slavim sa tobom!</h2>
              <p className="text-sm mb-2 text-gray-600">Da znaš da sam verovala u taj korak i pre nego što si ga napravila.</p>
              <p className="text-sm text-gray-600">Nagradi se, proslavi i idemo dalje.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChallengesView({ user, settings }) {
  const [challenges, setChallenges] = useState([]);
  const [acceptedChallengeIds, setAcceptedChallengeIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');

  const effectiveQuarter = getEffectiveQuarter(settings);
  useEffect(() => {
    loadChallenges();
  }, [effectiveQuarter]);

  const loadChallenges = async () => {
    try {
      setLoading(true);
      const quarter = getEffectiveQuarter(settings);
      if (!quarter) { setLoading(false); return; }

      // Get all active challenges
      const challengesSnapshot = await getDocs(
        query(collection(db, 'challenges'), where('isActive', '==', true))
      );
      setChallenges(challengesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Get user's accepted challenges for current quarter
      const acceptedSnapshot = await getDocs(
        query(
          collection(db, 'quarters', quarter, 'acceptedChallenges'),
          where('memberId', '==', user.id)
        )
      );
      setAcceptedChallengeIds(acceptedSnapshot.docs.map(doc => doc.data().challengeId));
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading challenges:', error);
      setLoading(false);
    }
  };

  const handleAcceptChallenge = async (challenge) => {
    try {
      // Check if already accepted
      if (acceptedChallengeIds.includes(challenge.id)) {
        // console.log('Već ste prihvatili ovaj izazov');
      return;
    }

      const quarter = getEffectiveQuarter(settings);
      if (!quarter) return;
      // Add to accepted challenges
      await addDoc(collection(db, 'quarters', quarter, 'acceptedChallenges'), {
          memberId: user.id,
        memberName: user.name,
        challengeId: challenge.id,
        challengeTitle: challenge.title,
        challengeDescription: challenge.description,
        challengePoints: challenge.points,
        challengeDeadline: challenge.deadline,
        challengeMaterialLink: challenge.materialLink || '',
        challengeTags: challenge.tags || [],
        requiresProof: challenge.requiresProof || false,
        acceptedAt: new Date().toISOString()
      });

      // console.log(`Uspešno ste prihvatili izazov: ${challenge.title}`);
      loadChallenges(); // Refresh the list
        } catch (error) {
      console.error('Error accepting challenge:', error);
      // console.log('Greška prilikom prihvatanja izazova');
    }
  };

  const handleUnacceptChallenge = async (challenge) => {
    try {
      const quarter = getEffectiveQuarter(settings);
      if (!quarter) return;
      // Find and delete the accepted challenge record
      const acceptedSnapshot = await getDocs(
        query(
          collection(db, 'quarters', quarter, 'acceptedChallenges'),
          where('memberId', '==', user.id),
          where('challengeId', '==', challenge.id)
        )
      );

      if (!acceptedSnapshot.empty) {
        await deleteDoc(acceptedSnapshot.docs[0].ref);
        // console.log(`Izazov "${challenge.title}" je uklonjen iz vašeg fokusa`);
        loadChallenges(); // Refresh the list
      }
      } catch (error) {
      console.error('Error unaccepting challenge:', error);
      // console.log('Greška prilikom uklanjanja izazova');
    }
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Učitavanje mikro-akcija...</div>
        </div>
      </div>
    );
  }

  const userLevel = user.nivo || 'Osnovni';
  const filteredChallenges = challenges.filter((c) => {
    const L = (c.levels && c.levels.length) ? c.levels : [c.level || 'Osnovni'];
    return L.includes(userLevel);
  });

  const tagsInList = [...new Set(filteredChallenges.flatMap((c) => c.tags || []))].sort();
  const displayChallenges = filteredChallenges.filter((c) => {
    const search = (searchText || '').toLowerCase().trim();
    if (search) {
      const title = (c.title || '').toLowerCase();
      const desc = (c.description || '').replace(/<[^>]+>/g, ' ').toLowerCase();
      if (!title.includes(search) && !desc.includes(search)) return false;
    }
    if (selectedTags.length > 0) {
      const challengeTags = (c.tags || []).map((t) => t.toLowerCase());
      const hasTag = selectedTags.some((t) => challengeTags.includes(t.toLowerCase()));
      if (!hasTag) return false;
    }
    if (statusFilter === 'accepted' && !acceptedChallengeIds.includes(c.id)) return false;
    if (statusFilter === 'not_accepted' && acceptedChallengeIds.includes(c.id)) return false;
    return true;
  });

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  return (
    <div className="p-4 lg:p-8">
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-2">Izaberi mikro-akcije</h1>
        <p className="text-gray-600 text-sm lg:text-base">
        Klikom na "Prihvati" prihvataš mikro-akcije na kojima želiš da radiš. 
        Ako se predomisliš, dovoljno je da klikneš "Ukloni mikro-akciju".
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 p-4 bg-white rounded-xl shadow border border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Filteri</h3>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Pretraga</label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Naslov ili opis..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent text-sm bg-white"
            >
              <option value="all">Sve</option>
              <option value="accepted">Prihvaćene</option>
              <option value="not_accepted">Neprihvaćene</option>
            </select>
          </div>
          {tagsInList.length > 0 && (
            <div className="w-full lg:w-auto">
              <label className="block text-xs font-medium text-gray-500 mb-2">Tagovi</label>
              <div className="flex flex-wrap gap-2">
                {tagsInList.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                      selectedTags.includes(tag)
                        ? 'bg-[#931b1d] text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
                {selectedTags.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedTags([])}
                    className="px-3 py-1.5 rounded-full text-xs font-medium text-gray-500 hover:text-gray-700 underline"
                  >
                    Obriši tagove
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Prikazano: {displayChallenges.length} od {filteredChallenges.length} mikro-akcija
        </p>
      </div>

      {/* Challenges List (by user level) */}
      <div className="space-y-4 lg:space-y-6">
        {displayChallenges.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-6 lg:p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm lg:text-base">
              {filteredChallenges.length === 0
                ? 'Nema dostupnih mikro-akcija u ovom trenutku'
                : 'Nema mikro-akcija koje odgovaraju filterima'}
            </p>
            <p className="text-gray-400 text-xs lg:text-sm mt-2">
              {filteredChallenges.length === 0 ? 'Proverite ponovo kasnije' : 'Promenite filtere da vidite više'}
            </p>
          </div>
        ) : (
          displayChallenges.map((challenge) => {
            const isAccepted = acceptedChallengeIds.includes(challenge.id);
            return (
              <div
                key={challenge.id}
                className={`bg-white rounded-xl shadow p-4 lg:p-6 transition-all duration-200 ${
                  isAccepted ? 'border-l-4' : ''
                } hover:shadow-lg`}
                style={isAccepted ? { borderLeftColor: '#931b1d', backgroundColor: '#931b1d10' } : {}}
              >
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                    <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-base lg:text-lg font-semibold text-gray-800">
                        {challenge.title || 'Bez naslova'}
                      </h3>
                      {isAccepted && (
                        <div className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: '#931b1d20', color: '#931b1d' }}>
                          Prihvaćen
                        </div>
                      )}
                    </div>
                    <MicroActionBody
                      description={challenge.description}
                      materialLink={challenge.materialLink}
                      tags={challenge.tags}
                      className="mb-3"
                    />
                    {challenge.deadline && (
                      <p className="text-xs lg:text-sm text-gray-500 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Rok: {new Date(challenge.deadline).toLocaleDateString('sr-RS')}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col lg:items-end gap-3">
                    <div className="text-xl lg:text-2xl font-bold text-[#931b1d] text-center lg:text-right">
                      {challenge.points || 0} poena
                    </div>
                    <div className="flex gap-2">
                      {isAccepted ? (
                        <button
                          onClick={() => handleUnacceptChallenge(challenge)}
                          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition text-sm font-medium"
                        >
                          Ukloni mikro-akciju
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAcceptChallenge(challenge)}
                          className="bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d]/90 transition text-sm font-medium"
                        >
                          Prihvati mikro-akciju
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function FocusZonaView({ user, settings }) {
  const [focusZones, setFocusZones] = useState([]);
  const [focusZonesLoading, setFocusZonesLoading] = useState(true);
  const [focusZonesError, setFocusZonesError] = useState(null);
  const [sectionHeader, setSectionHeader] = useState({ title: 'Dobro došla u zonu fokusa.', description: 'Dobro došla u fokus zonu.\nMesto gde prestaju distrakcije i počinju akcije.' });
  const [temaData, setTemaData] = useState(null);

  useEffect(() => {
    loadFocusZones();
    loadSectionHeader();
    loadTema();
  }, []);

  const loadFocusZones = async () => {
    try {
      setFocusZonesLoading(true);
      setFocusZonesError(null);
      const focusZonesSnapshot = await getDocs(collection(db, 'focusZones'));
      const zones = focusZonesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(z => z.isActive !== false)
        .sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateB - dateA;
        });
      setFocusZones(zones);
    } catch (error) {
      console.error('Error loading focus zones:', error);
      setFocusZonesError('Greška prilikom učitavanja fokus zona. Pokušaj ponovo.');
    } finally {
      setFocusZonesLoading(false);
    }
  };

  const loadSectionHeader = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'settings', 'focusZonaSection'));
      if (docSnap.exists() && docSnap.data()) {
        const d = docSnap.data();
        setSectionHeader({ title: d.title || 'Dobro došla u zonu fokusa.', description: d.description ?? 'Dobro došla u fokus zonu.\nMesto gde prestaju distrakcije i počinju akcije.' });
      }
    } catch (e) {
      console.error('Error loading focus section header:', e);
    }
  };

  const loadTema = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'settings', 'focusZonaTema'));
      if (docSnap.exists() && docSnap.data()) setTemaData(docSnap.data());
    } catch (e) {
      console.error('Error loading focus zona tema:', e);
    }
  };

  const getScheduleText = (zone, fallback) => {
    if (!zone) return fallback;
    const date = zone.date ? new Date(zone.date) : null;
    const weekday = date ? date.toLocaleDateString('sr-Latn-RS', { weekday: 'long' }).toUpperCase() : '';
    const time = zone.time ? zone.time.toUpperCase() : '';
    if (weekday && time) return `${weekday}, ${time}`;
    if (weekday) return weekday;
    if (time) return time;
    return fallback;
  };

  const getSafeLink = (link) => {
    if (!link) return null;
    const trimmed = String(link).trim();
    if (!trimmed) return null;
    if (!/^https?:\/\//i.test(trimmed)) return `https://${trimmed}`;
    return trimmed;
  };

  const getLevelStyle = (level) => {
    const l = (level || '').toUpperCase();
    if (l.includes('OSNOVNI')) return { backgroundColor: '#b9847a', color: '#fff' };
    if (l.includes('NAPREDNI')) return { backgroundColor: '#931b1d', color: '#fff' };
    if (l.includes('MASTERMIND')) return { backgroundColor: '#323433', color: '#fff' };
    return { backgroundColor: '#5c614d', color: '#fff' };
  };

  const renderZoneDescription = (desc) => {
    if (!desc) return null;
    const isHtml = typeof desc === 'string' && desc.includes('<');
    const safeHtml = isHtml ? sanitizeHtml(desc) : '';
    if (isHtml && safeHtml) return <div className="prose prose-sm max-w-none text-gray-600 [&_a]:text-[#931b1d] [&_a]:font-medium [&_a:hover]:underline" dangerouslySetInnerHTML={{ __html: safeHtml }} />;
    return <p className="text-gray-600 text-sm lg:text-base leading-relaxed whitespace-pre-wrap">{desc}</p>;
  };

  return (
    <div className="p-4 lg:p-8 bg-gray-50 min-h-screen">
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">{sectionHeader.title}</h1>
        <p className="text-base lg:text-lg text-gray-600 whitespace-pre-wrap">{sectionHeader.description}</p>
      </div>

      {/* Focus Zones Grid - 2 per row */}
      <div className="space-y-8">
        {focusZonesLoading && (
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 text-center text-gray-500">Učitavanje fokus zona...</div>
        )}
        {focusZonesError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{focusZonesError}</div>
        )}
        {!focusZonesLoading && !focusZonesError && focusZones.length === 0 && (
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 text-center text-gray-500">Trenutno nema aktivnih fokus zona.</div>
        )}
        {!focusZonesLoading && focusZones.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {focusZones.map((zone) => (
              <div key={zone.id} className="bg-white rounded-xl shadow-sm p-6 flex flex-col">
                <div className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">
                  {getScheduleText(zone, '')}
                </div>
                <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">{zone.title}</h2>
                {(zone.eventLevels || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {zone.eventLevels.map((level, idx) => (
                      <span key={idx} className="text-white px-3 py-1 rounded text-xs font-semibold uppercase tracking-wide" style={getLevelStyle(level)}>{level}</span>
                    ))}
                  </div>
                )}
                <div className="flex-1 mb-6">
                  {renderZoneDescription(zone.description)}
                </div>
                <div className="mt-auto">
                  <button
                    onClick={() => { const safeLink = getSafeLink(zone.link); if (safeLink) window.open(safeLink, '_blank'); }}
                    disabled={!zone.link}
                    className="text-white rounded-lg font-semibold uppercase tracking-wide hover:opacity-90 transition disabled:opacity-50 w-full py-3 px-5 text-sm"
                    style={{ backgroundColor: '#931b1d', fontSize: '1.2em' }}
                  >
                    PRIKLJUČI SE
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tema focus zone section */}
      {temaData && (
        <div className="mt-12 lg:mt-16 rounded-2xl p-6 lg:p-8" style={{ backgroundColor: '#f5f0e8' }}>
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-8">
            {(temaData.title != null && temaData.title !== '') ? temaData.title : (`Tema focus zone za ${temaData.titleSuffix || ''}`.trim() || 'Tema focus zone')}
          </h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Vežba vizualizacije</h3>
              <div className="bg-white rounded-xl p-4 min-h-[100px] text-gray-700 whitespace-pre-wrap">{temaData.section1 || ''}</div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Predlog teme za pisanje za B2C | Digitalni proizvod</h3>
              <div className="bg-white rounded-xl p-4 min-h-[100px] text-gray-700 whitespace-pre-wrap">{temaData.section2 || ''}</div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Predlog teme za pisanje za B2C | Fizički proizvod</h3>
              <div className="bg-white rounded-xl p-4 min-h-[100px] text-gray-700 whitespace-pre-wrap">{temaData.section3 || ''}</div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Predlog teme za pisanje za B2B</h3>
              <div className="bg-white rounded-xl p-4 min-h-[100px] text-gray-700 whitespace-pre-wrap">{temaData.section4 || ''}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DogađajiView({ user, settings }) {
  const [levelFocus, setLevelFocus] = useState(null);

  const levelKey = user.nivo || 'Osnovni';

  useEffect(() => {
    const loadLevelFocus = async () => {
      try {
        const nivoSnap = await getDoc(doc(db, 'nivoFokus', levelKey));
        if (nivoSnap.exists() && nivoSnap.data()) {
          setLevelFocus(nivoSnap.data());
        } else {
          setLevelFocus(null);
        }
      } catch (_) {
        setLevelFocus(null);
      }
    };
    loadLevelFocus();
  }, [levelKey]);

  return (
    <div className="p-4 lg:p-8 bg-stone-50 min-h-screen">
      {/* Važni događaji za tvoj nivo - level-specific from nivoFokus */}
      {levelFocus && levelFocus.rightColumn?.items?.length > 0 && (
        <div className="rounded-2xl p-6 lg:p-8 mb-6 lg:mb-8" style={{ backgroundColor: '#f5f0e8' }}>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-4 lg:mb-6">Važni događaji za tvoj nivo</h2>
          <p className="text-sm text-gray-600 mb-4">Događaji za nivo: <span className="font-semibold text-gray-800">{levelKey}</span></p>
          <div className="space-y-3">
            {(levelFocus.rightColumn.items || []).filter((i) => i && (i.title || '').trim()).map((item, idx) => {
              const href = item.url && (item.url.startsWith('http') ? item.url : `https://${item.url}`);
              return (
                <div key={`level-event-${idx}`} className="flex gap-2 items-center">
                  <span className="flex-1 bg-white rounded-lg px-4 py-3 text-gray-800 text-sm truncate">{item.title}</span>
                  {href && (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="bg-gray-900 text-white text-sm font-medium px-4 py-3 rounded-lg whitespace-nowrap hover:bg-gray-800">
                      PRIKLJUČI SE
                    </a>
                  )}
                </div>
              );
            })}
          </div>
          {levelFocus.rightColumn.bottomLinkUrl && (
            <p className="mt-4 text-sm text-gray-700 flex items-center gap-2">
              <a href={levelFocus.rightColumn.bottomLinkUrl.startsWith('http') ? levelFocus.rightColumn.bottomLinkUrl : `https://${levelFocus.rightColumn.bottomLinkUrl}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-gray-800">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9a1 1 0 001.804.98l10-2A1 1 0 0018 13V3z" /></svg>
                Poslušaj Jelenin savet za tvoj biznis.
              </a>
            </p>
          )}
        </div>
      )}

    </div>
  );
}

// Fixed set of quarters for past-quarter filter (archived): 2025 Q1–Q4 and 2026 Q1.
const LEADERBOARD_ARCHIVED_QUARTERS = [
  { id: 'Q1-2025', label: 'Q1 2025 (jan–mar)' },
  { id: 'Q2-2025', label: 'Q2 2025 (apr–jun)' },
  { id: 'Q3-2025', label: 'Q3 2025 (jul–sep)' },
  { id: 'Q4-2025', label: 'Q4 2025 (okt–dec)' },
  { id: 'Q1-2026', label: 'Q1 2026 (jan–mar)' }
];

function LeaderboardView({ user, settings }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [deletingMemberId, setDeletingMemberId] = useState(null);
  // viewMode: 'current' = effective current quarter; 'custom' = sum of selected quarters
  const [viewMode, setViewMode] = useState('current');
  const [selectedQuarterIds, setSelectedQuarterIds] = useState([]);
  const [pendingQuarterIds, setPendingQuarterIds] = useState([]);
  const [selectionError, setSelectionError] = useState('');
  // Only quarters that have at least one points doc (actions done) – no empty quarters
  const [availableQuartersWithData, setAvailableQuartersWithData] = useState([]);

  const effectiveQuarter = getEffectiveQuarter(settings);
  const isViewingCurrent = viewMode === 'current';
  const displayLabel =
    isViewingCurrent || selectedQuarterIds.length === 0
      ? effectiveQuarter
      : selectedQuarterIds.join(' + ');

  useEffect(() => {
    loadLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, effectiveQuarter, JSON.stringify(selectedQuarterIds)]);

  // Load which quarters have points data (so we only show non-empty quarters in Prošli kvartali)
  useEffect(() => {
    let cancelled = false;
    const loadQuartersWithData = async () => {
      const withData = [];
      for (const q of LEADERBOARD_ARCHIVED_QUARTERS) {
        try {
          const snap = await getDocs(
            query(collection(db, 'quarters', q.id, 'points'), limit(1))
          );
          if (!snap.empty) withData.push(q);
        } catch (_) {
          // Quarter doc or subcollection may not exist
        }
      }
      if (!cancelled) setAvailableQuartersWithData(withData);
    };
    loadQuartersWithData();
    return () => { cancelled = true; };
  }, []);

  const loadLeaderboard = async () => {
    try {
      if (viewMode === 'current') {
        const quarter = getEffectiveQuarter(settings);
        if (!quarter) return;
        await ensureQuarterExists(db, quarter);
        const pointsSnapshot = await getDocs(
          query(
            collection(db, 'quarters', quarter, 'points'),
            orderBy('totalPoints', 'desc')
          )
        );
        const memberIdField = 'memberId';
        const leaderboardData = await Promise.all(
          pointsSnapshot.docs.map(async (pointDoc) => {
            const memberId = pointDoc.data()[memberIdField] ?? pointDoc.id;
            const memberDoc = await getDoc(doc(db, 'members', memberId));
            return {
              ...pointDoc.data(),
              memberId,
              member: memberDoc.exists() ? memberDoc.data() : null
            };
          })
        );
        setLeaderboard(leaderboardData);
        return;
      }

      // Custom view: sum points across selected quarters
      if (!selectedQuarterIds || selectedQuarterIds.length === 0) {
        setLeaderboard([]);
        return;
      }

      const totalsByMember = {};
      const memberIdField = 'memberId';

      for (const quarter of selectedQuarterIds) {
        await ensureQuarterExists(db, quarter);
        const snap = await getDocs(collection(db, 'quarters', quarter, 'points'));
        snap.forEach((pointDoc) => {
          const data = pointDoc.data() || {};
          const memberId = data[memberIdField] ?? pointDoc.id;
          const pts = data.totalPoints || 0;
          if (!memberId) return;
          if (!totalsByMember[memberId]) {
            totalsByMember[memberId] = 0;
          }
          totalsByMember[memberId] += pts;
        });
      }

      const entries = Object.entries(totalsByMember)
        .map(([memberId, totalPoints]) => ({ memberId, totalPoints }))
        .sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0));

      const leaderboardData = await Promise.all(
        entries.map(async (entry) => {
          const memberDoc = await getDoc(doc(db, 'members', entry.memberId));
          return {
            memberId: entry.memberId,
            totalPoints: entry.totalPoints,
            member: memberDoc.exists() ? memberDoc.data() : null
          };
        })
      );

      setLeaderboard(leaderboardData);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const isUnknownMember = (entry) => !(entry.member?.name && (entry.member.name || '').trim());

  const handleDeleteMemberAndData = async (memberId) => {
    if (!user.isAdmin || !memberId) return;
    if (!window.confirm('Obrisati ovog člana (Nepoznat član) i sve njegove podatke (poeni, aktivnosti, pobede)? Ovo se ne može poništiti.')) return;
    setDeletingMemberId(memberId);
    try {
      const quarter = getEffectiveQuarter(settings);
      await deleteMemberAndAllData(db, memberId, quarter);
      await loadLeaderboard();
    } catch (error) {
      console.error('Error deleting member and data:', error);
    } finally {
      setDeletingMemberId(null);
    }
  };

  const nextQuarterStart = getNextQuarterStartText(effectiveQuarter);

  const togglePendingQuarter = (id) => {
    setSelectionError('');
    setPendingQuarterIds((prev) =>
      prev.includes(id) ? prev.filter((q) => q !== id) : [...prev, id]
    );
  };

  const applyQuarterSelection = () => {
    if (!pendingQuarterIds || pendingQuarterIds.length === 0) {
      setSelectionError('Izaberite bar jedan kvartal za prikaz zbira poena.');
      return;
    }
    setSelectionError('');
    setSelectedQuarterIds(pendingQuarterIds);
    setViewMode('custom');
  };

  const switchToCurrentQuarter = () => {
    setSelectionError('');
    setPendingQuarterIds([]);
    setViewMode('current');
  };

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-2">Rang Lista</h1>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          type="button"
          onClick={switchToCurrentQuarter}
          className={`px-4 py-2 rounded-lg font-medium transition ${isViewingCurrent ? 'bg-[#931b1d] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          Trenutni kvartal
        </button>
        <span className="text-gray-400">|</span>
        <span className="text-sm text-gray-600">Prošli kvartali (zbirovi):</span>
        {availableQuartersWithData.length === 0 ? (
          <span className="text-xs text-gray-500">Nema arhiviranih kvartala sa poenima</span>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {availableQuartersWithData.map((q) => (
                <label key={q.id} className="inline-flex items-center gap-1 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={pendingQuarterIds.includes(q.id)}
                    onChange={() => togglePendingQuarter(q.id)}
                    className="rounded border-gray-300 text-[#931b1d] focus:ring-[#931b1d]"
                  />
                  <span>{q.label}</span>
                </label>
              ))}
            </div>
            <button
              type="button"
              onClick={applyQuarterSelection}
              className="px-4 py-2 rounded-lg font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
            >
              Prikaži
            </button>
          </>
        )}
      </div>

      {selectionError && (
        <p className="text-xs text-red-600 mb-2">{selectionError}</p>
      )}

      <p className="text-gray-600 mb-1">
        Rangiranje za: <strong>{displayLabel}</strong>
        {isViewingCurrent && nextQuarterStart && (
          <span className="text-sm text-gray-500 ml-2">(sledeći kvartal počinje {nextQuarterStart})</span>
        )}
      </p>
      <div className="mb-8" />

      {leaderboard.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <p className="text-gray-500 mb-2">Nema podataka za {displayLabel}</p>
          <p className="text-gray-400 text-sm">
            {isViewingCurrent
              ? 'Bodovi će se prikazati kada članovi počnu sa aktivnostima u tom kvartalu.'
              : 'Bodovi će se prikazati kada članovi imaju poene u izabranim kvartalima.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Rang</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Član</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Poeni</th>
                {user.isAdmin && isViewingCurrent && <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Akcije</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {leaderboard.map((entry, index) => (
                <tr 
                  key={entry.memberId} 
                  className={entry.memberId === user.id ? 'bg-[#931b1d]/10' : 'hover:bg-gray-50'}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {index === 0 && <span className="text-2xl mr-2">🥇</span>}
                      {index === 1 && <span className="text-2xl mr-2">🥈</span>}
                      {index === 2 && <span className="text-2xl mr-2">🥉</span>}
                      <span className="font-semibold text-gray-800">#{index + 1}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-3">
                        {entry.member?.avatarUrl ? (
                          <img 
                            src={entry.member.avatarUrl} 
                            alt={getDisplayName(entry.member) || 'User'} 
                            className="w-10 h-10 rounded-full object-cover" 
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#931b1d]/30 flex items-center justify-center text-[#931b1d] font-semibold">
                            {getInitials(entry.member?.name)}
                          </div>
                        )}
                        <span className="font-medium text-gray-800">
                          {entry.member?.name || 'Nepoznat član'}
                        </span>
                      </div>
                      {user.isAdmin && isUnknownMember(entry) && (
                        <span className="text-xs text-gray-500 mt-0.5">ID: {entry.memberId}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-xl font-bold text-[#931b1d]">{entry.totalPoints || 0}</span>
                  </td>
                  {user.isAdmin && isViewingCurrent && (
                    <td className="px-6 py-4 text-right">
                      {isUnknownMember(entry) && (
                        <button
                          type="button"
                          onClick={() => handleDeleteMemberAndData(entry.memberId)}
                          disabled={deletingMemberId === entry.memberId}
                          className="text-sm text-[#931b1d] hover:underline disabled:opacity-50"
                        >
                          {deletingMemberId === entry.memberId ? 'Brisanje...' : 'Obriši člana i podatke'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VictoriesView({ user, settings }) {
  const [victories, setVictories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVictories();
  }, []);

  const loadVictories = async () => {
    try {
      const victoriesSnapshot = await getDocs(
        query(collection(db, 'victories'), orderBy('timestamp', 'desc'))
      );
      
      // Load user data for each victory to get avatars and reactions
      const victoriesData = await Promise.all(
        victoriesSnapshot.docs.map(async (victoryDoc) => {
          const data = victoryDoc.data();
          
          // Use avatarUrl from victory data, or fetch it if not present
          let avatarUrl = data.avatarUrl || null;
          if (!avatarUrl && data.memberId) {
            try {
              const memberDoc = await getDoc(doc(db, 'members', data.memberId));
              if (memberDoc.exists()) {
                avatarUrl = memberDoc.data().avatarUrl;
              }
            } catch (error) {
              console.error('Error loading member data:', error);
            }
          }
          
          // Check which reaction types the current user has clicked
          let userReactions = {};
          try {
            const reactionsSnapshot = await getDocs(collection(db, 'victories', victoryDoc.id, 'reactions'));
            reactionsSnapshot.docs.forEach(reactionDoc => {
              const reactionData = reactionDoc.data();
              if (reactionData.userId === user.id) {
                userReactions[reactionData.reactionType] = true;
              }
            });
          } catch (error) {
            console.error('Error checking reaction:', error);
          }
          
          // Fetch all reactions for this victory to count them
          let reactionCounts = {
            'thumbs-up': 0,
            'clap': 0,
            'love': 0,
            'heart': 0,
            'lightbulb': 0,
            'smile': 0
          };
          try {
            const reactionsSnapshot = await getDocs(collection(db, 'victories', victoryDoc.id, 'reactions'));
            reactionsSnapshot.docs.forEach(reactionDoc => {
              const reactionType = reactionDoc.data().reactionType;
              if (reactionCounts.hasOwnProperty(reactionType)) {
                reactionCounts[reactionType]++;
              }
            });
          } catch (error) {
            console.error('Error loading reactions:', error);
          }
          
          return { id: victoryDoc.id, ...data, avatarUrl, userReactions, reactionCounts };
        })
      );
      
      setVictories(victoriesData);
    } catch (error) {
      console.error('Error loading victories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReaction = async (victoryId, reactionType) => {
    try {
      // Use a compound key: user-reactionType
      const reactionRef = doc(db, 'victories', victoryId, 'reactions', `${user.id}_${reactionType}`);
      
      // Check if user already reacted to this specific reaction type
      const reactionDoc = await getDoc(reactionRef);
      
      if (reactionDoc.exists()) {
        // User already clicked this icon, remove the reaction
        await deleteDoc(reactionRef);
      } else {
        // Add the reaction
        await setDoc(reactionRef, {
          userId: user.id,
          userName: user.name,
          reactionType: reactionType,
          timestamp: new Date().toISOString()
        });
      }
      
      // Reload victories to update UI
      loadVictories();
    } catch (error) {
      console.error('Error toggling reaction:', error);
      // console.log('Greška prilikom reagovanja');
    }
  };

  const getReactionIconUrl = (type) => {
    const iconUrls = {
      'thumbs-up': 'https://www.jelenadelakorda.com/wp-content/uploads/2025/10/Thumbsup.png',
      'clap': 'https://www.jelenadelakorda.com/wp-content/uploads/2025/10/Clap.png',
      'love': 'https://www.jelenadelakorda.com/wp-content/uploads/2025/10/Love.png',
      'heart': 'https://www.jelenadelakorda.com/wp-content/uploads/2025/10/Support.png',
      'lightbulb': 'https://www.jelenadelakorda.com/wp-content/uploads/2025/10/Idea.png',
      'smile': 'https://www.jelenadelakorda.com/wp-content/uploads/2025/10/Smile.png'
    };
    
    return iconUrls[type] || iconUrls['thumbs-up'];
  };

  const handleDeleteVictory = async (victoryId, victoryTitle) => {
    if (!user.isAdmin) {
      // console.log('Nemate dozvolu za brisanje pobeda');
      return;
    }

    const confirmDelete = window.confirm(`Da li ste sigurni da želite da obrišete pobedu "${victoryTitle}"?`);
    if (!confirmDelete) {
      return;
    }

    try {
      // Delete all reactions for this victory first
      const reactionsSnapshot = await getDocs(collection(db, 'victories', victoryId, 'reactions'));
      const deleteReactionsPromises = reactionsSnapshot.docs.map(reactionDoc => 
        deleteDoc(reactionDoc.ref)
      );
      await Promise.all(deleteReactionsPromises);

      // Delete the victory document
      await deleteDoc(doc(db, 'victories', victoryId));
      
      // console.log('Pobeda je uspešno obrisana');
      loadVictories(); // Refresh the list
    } catch (error) {
      console.error('Error deleting victory:', error);
      // console.log('Greška prilikom brisanja pobede');
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <div className="p-4 lg:p-8">
      <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6 lg:mb-8">Naše male pobede</h1>
      <p className="text-gray-600 text-sm lg:text-base mb-6 lg:mb-8 leading-relaxed">
        Velike promene dolaze kao posledica malih pobeda koje često ne stignemo da proslavimo. Hajde da to promenimo od sada do zauvek.
      </p>

      {loading ? (
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <p className="text-gray-500">Učitavanje pobeda...</p>
        </div>
      ) : victories.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <p className="text-gray-500 mb-2">Još nema podeljenih pobeda</p>
          <p className="text-gray-400 text-sm">Budi prva koja podeli svoju pobedu!</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:gap-6">
          {victories.map(victory => {
            const userReactions = victory.userReactions || {};
            
            const reactionTypes = [
              { type: 'thumbs-up', color: '#0077B5', bg: '#0077B51A' },
              { type: 'clap', color: '#008F00', bg: '#008F001A' },
              { type: 'love', color: '#A970D1', bg: '#A970D11A' },
              { type: 'heart', color: '#E74C3C', bg: '#E74C3C1A' },
              { type: 'lightbulb', color: '#FFB900', bg: '#FFB9001A' },
              { type: 'smile', color: '#0CA6FF', bg: '#0CA6FF1A' }
            ];
            
            return (
              <div key={victory.id} className="bg-white rounded-xl shadow p-4 lg:p-6">
                <div className="flex items-start gap-3 lg:gap-4 mb-3">
                  {victory.avatarUrl ? (
                    <img src={victory.avatarUrl} alt={victory.memberName} className="w-10 h-10 lg:w-12 lg:h-12 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center text-white text-sm lg:text-base font-bold flex-shrink-0" style={{ backgroundColor: '#931b1d' }}>
                      {getInitials(victory.memberName)}
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 text-sm lg:text-base">{victory.memberName}</h3>
                    <p className="text-xs lg:text-sm text-gray-500">{new Date(victory.timestamp).toLocaleString('sr-RS')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                  <span className="bg-[#5c614d]/10 text-[#5c614d] text-xs px-2 lg:px-3 py-1 rounded-full font-medium">
                    {victory.challengeTitle}
                  </span>
                    {user.isAdmin && (
                      <button
                        onClick={() => handleDeleteVictory(victory.id, victory.challengeTitle)}
                        className="text-red-500 hover:text-red-700 transition-colors p-1 rounded-full hover:bg-red-50"
                        title="Obriši pobedu"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-sm lg:text-base text-gray-700 whitespace-pre-wrap mb-3">{victory.victoryText}</p>
                {victory.victoryLink && (
                  <a
                    href={victory.victoryLink.startsWith('http') ? victory.victoryLink : `https://${victory.victoryLink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-[#931b1d] hover:underline mb-3"
                  >
                    Link
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                  </a>
                )}
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex items-center gap-1 flex-wrap">
                    {reactionTypes.map((reaction) => {
                      const isSelected = userReactions[reaction.type] || false;
                      const count = victory.reactionCounts?.[reaction.type] || 0;
                      return (
                        <button
                          key={reaction.type}
                          onClick={() => handleReaction(victory.id, reaction.type)}
                          className={`px-2 py-1.5 rounded-full flex items-center gap-1.5 transition hover:scale-105 ${
                            isSelected 
                              ? 'ring-1 ring-offset-1 ring-gray-300' 
                              : ''
                          }`}
                          style={{ backgroundColor: isSelected ? reaction.bg : 'transparent' }}
                        >
                          <img 
                            src={getReactionIconUrl(reaction.type)} 
                            alt={reaction.type}
                            className="w-5 h-5"
                          />
                          {count > 0 && (
                            <span className="text-xs font-medium" style={{ color: reaction.color }}>
                              {count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProfileView({ user, settings, db, storage, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile) {
      // console.log('Molimo izaberite sliku');
      return;
    }

    setLoading(true);
    try {
      // Upload avatar to Firebase Storage
      const avatarRef = ref(storage, `avatars/${Date.now()}_${avatarFile.name}`);
      await uploadBytes(avatarRef, avatarFile);
      const avatarUrl = await getDownloadURL(avatarRef);

      // Update user document in Firestore
      await updateDoc(doc(db, 'members', user.id), {
        avatarUrl: avatarUrl
      });

      // console.log('Profilna slika je uspešno ažurirana!');
      setAvatarFile(null);
      setPreviewUrl(null);
      
      // Refresh user data
      onRefresh();
    } catch (error) {
      console.error('Error uploading avatar:', error);
      // console.log('Greška prilikom upload-a slike');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <div className="p-4 lg:p-8">
      <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6 lg:mb-8">Moj profil</h1>

      <div className="bg-white rounded-xl shadow p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row items-center gap-6">
          {/* Avatar Section */}
          <div className="flex flex-col items-center">
            {previewUrl ? (
              <img src={previewUrl} alt="Preview" className="w-32 h-32 rounded-full object-cover border-4" style={{ borderColor: '#931b1d' }} />
            ) : user.avatarUrl ? (
              <img src={user.avatarUrl} alt={user.name} className="w-32 h-32 rounded-full object-cover border-4" style={{ borderColor: '#931b1d' }} />
            ) : (
              <div className="w-32 h-32 rounded-full flex items-center justify-center text-white text-4xl font-bold border-4" style={{ backgroundColor: '#931b1d', borderColor: '#931b1d' }}>
                {getInitials(user.name)}
              </div>
            )}
            
            <div className="mt-4 space-y-2">
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
                id="avatar-upload"
              />
              <label
                htmlFor="avatar-upload"
                className="block px-4 py-2 text-sm font-medium text-white rounded-lg cursor-pointer transition hover:opacity-80"
                style={{ backgroundColor: '#5c614d' }}
              >
                Izaberite sliku
              </label>
              
              {avatarFile && (
                <button
                  onClick={handleUploadAvatar}
                  disabled={loading}
                  className="w-full px-4 py-2 text-sm font-medium text-white rounded-lg transition disabled:opacity-50"
                  style={{ backgroundColor: '#931b1d' }}
                >
                  {loading ? 'Upload...' : 'Sačuvaj sliku'}
                </button>
              )}
            </div>
          </div>

          {/* User Info */}
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">{user.name}</h2>
            <p className="text-gray-600 mb-4">{user.email}</p>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Status:</span>
                <span className="text-sm px-2 py-1 rounded-full" style={{ backgroundColor: user.isAdmin ? '#931b1d' : '#5c614d', color: 'white' }}>
                  {user.isAdmin ? 'Admin' : 'Član'}
                </span>
              </div>
              
              {user.joinedDate && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Pridružio se:</span>
                  <span className="text-sm text-gray-600">
                    {new Date(user.joinedDate).toLocaleDateString('sr-RS')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminView({ user, settings, db, storage, onRefreshSettings, onRefreshMembers }) {
  const [activeTab, setActiveTab] = useState('members');

  return (
    <div className="p-4 lg:p-8">
      <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6 lg:mb-8">Admin Panel</h1>

      {/* Tab Navigation - single row, scroll on narrow screens */}
      <div className="flex flex-nowrap gap-1 lg:gap-2 mb-6 lg:mb-8 border-b border-gray-200 overflow-x-auto pb-px min-w-0">
        <button
          onClick={() => setActiveTab('members')}
          className={`px-2 lg:px-4 py-2 font-medium transition border-b-2 text-xs lg:text-sm whitespace-nowrap shrink-0 ${
            activeTab === 'members'
              ? 'border-[#931b1d] text-[#931b1d]'
              : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
        >
          Članovi
        </button>
        <button
          onClick={() => setActiveTab('challenges')}
          className={`px-2 lg:px-4 py-2 font-medium transition border-b-2 text-xs lg:text-sm whitespace-nowrap shrink-0 ${
            activeTab === 'challenges'
              ? 'border-[#931b1d] text-[#931b1d]'
              : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
        >
          Mikro-akcije
        </button>
        <button
          onClick={() => setActiveTab('events')}
          className={`px-2 lg:px-4 py-2 font-medium transition border-b-2 text-xs lg:text-sm whitespace-nowrap shrink-0 ${
            activeTab === 'events'
              ? 'border-[#931b1d] text-[#931b1d]'
              : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
        >
          Događaji
        </button>
        <button
          onClick={() => setActiveTab('fokus')}
          className={`px-2 lg:px-4 py-2 font-medium transition border-b-2 text-xs lg:text-sm whitespace-nowrap shrink-0 ${
            activeTab === 'fokus'
              ? 'border-[#931b1d] text-[#931b1d]'
              : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
        >
          Fokus zona
        </button>
        <button
          onClick={() => setActiveTab('nivoFokus')}
          className={`px-2 lg:px-4 py-2 font-medium transition border-b-2 text-xs lg:text-sm whitespace-nowrap shrink-0 ${
            activeTab === 'nivoFokus'
              ? 'border-[#931b1d] text-[#931b1d]'
              : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
        >
          Nivo fokus
        </button>
        <button
          onClick={() => setActiveTab('points')}
          className={`px-2 lg:px-4 py-2 font-medium transition border-b-2 text-xs lg:text-sm whitespace-nowrap shrink-0 ${
            activeTab === 'points'
              ? 'border-[#931b1d] text-[#931b1d]'
              : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
        >
          Bodovi
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-2 lg:px-4 py-2 font-medium transition border-b-2 text-xs lg:text-sm whitespace-nowrap shrink-0 ${
            activeTab === 'settings'
              ? 'border-[#931b1d] text-[#931b1d]'
              : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
        >
          Postavke
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'members' && <AdminMembers db={db} storage={storage} onRefresh={onRefreshMembers} settings={settings} />}
      {activeTab === 'challenges' && <AdminChallenges db={db} />}
      {activeTab === 'events' && <AdminEvents db={db} />}
      {activeTab === 'fokus' && <AdminFokusZona db={db} />}
      {activeTab === 'nivoFokus' && <AdminNivoFokus db={db} />}
      {activeTab === 'points' && <AdminPoints db={db} settings={settings} />}
      {activeTab === 'settings' && <AdminSettings db={db} settings={settings} onRefresh={onRefreshSettings} />}
    </div>
  );
}

// Members Management Component
function AdminMembers({ db, storage, onRefresh, settings }) {
  const [members, setMembers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const NIVO_OPTIONS = ['Osnovni', 'Napredni', 'Mastermind'];
  const [newMember, setNewMember] = useState({ name: '', surname1: '', surname2: '', email: '', isAdmin: false, nivo: 'Osnovni' });
  const [avatarFile, setAvatarFile] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Challenge tracking state
  const [challengeActivities, setChallengeActivities] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedChallengeId, setSelectedChallengeId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [pointsAdjustment, setPointsAdjustment] = useState({ memberId: '', adjustment: '', reason: '' });
  const [challenges, setChallenges] = useState([]);
  const [csvImportModal, setCsvImportModal] = useState(false);
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [isEditingLevels, setIsEditingLevels] = useState(false);
  const [levelEdits, setLevelEdits] = useState({});
  const [savingLevels, setSavingLevels] = useState(false);

  useEffect(() => {
    loadMembers();
    loadChallengeActivities();
    loadChallenges();
  }, [settings]);

  const loadMembers = async () => {
    try {
      const membersSnapshot = await getDocs(collection(db, 'members'));
      setMembers(membersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  const loadChallengeActivities = async () => {
    try {
      // console.log('🔍 Loading challenge activities for quarter:', settings.currentQuarter);
      
      // First get all activities, then filter and sort in JavaScript to avoid index requirement
      const activitiesSnapshot = await getDocs(
        collection(db, 'quarters', settings.currentQuarter, 'activities')
      );

      // console.log('📊 Found total activities:', activitiesSnapshot.size);
      
      // Filter for challenge activities and sort by timestamp
      const challengeActivities = activitiesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(activity => activity.type === 'challenge')
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      // console.log('📊 Found challenge activities:', challengeActivities.length);

      const activitiesWithDetails = await Promise.all(
        challengeActivities.map(async (activity) => {
          // console.log('Activity data:', activity);

          // Get member details
          const memberDoc = await getDoc(doc(db, 'members', activity.memberId));
          const member = memberDoc.exists() ? memberDoc.data() : null;

          // Get challenge details
          const challengeDoc = await getDoc(doc(db, 'challenges', activity.challengeId));
          const challenge = challengeDoc.exists() ? challengeDoc.data() : null;

          return {
            id: activity.id,
            memberId: activity.memberId,
            memberName: member ? getDisplayName(member) : 'Unknown Member',
            challengeId: activity.challengeId,
            challengeTitle: challenge ? challenge.title : 'Unknown Challenge',
            points: activity.points,
            proofUrl: activity.proofUrl || null,
            timestamp: activity.timestamp,
            type: activity.type
          };
        })
      );

      // console.log('✅ Processed challenge activities:', activitiesWithDetails);
      setChallengeActivities(activitiesWithDetails);
    } catch (error) {
      console.error('❌ Error loading challenge activities:', error);
    }
  };

  const loadChallenges = async () => {
    try {
      // console.log('🔍 Loading challenges...');
      const challengesSnapshot = await getDocs(collection(db, 'challenges'));
      // console.log('📊 Found challenges:', challengesSnapshot.size);
      const challengesList = challengesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // console.log('✅ Challenges loaded:', challengesList);
      setChallenges(challengesList);
    } catch (error) {
      console.error('❌ Error loading challenges:', error);
    }
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      parseCsvFile(file);
    } else {
      // console.log('Molimo odaberite validan CSV fajl');
    }
  };

  const parseCsvFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        // console.log('CSV fajl mora imati najmanje header i jedan red podataka');
        return;
      }

      const header = lines[0].split(',').map(h => h.trim().toLowerCase());
      const newFormatHeaders = ['name', 'surname1', 'surname2', 'email'];
      const oldFormatHeaders = ['name', 'surname', 'email'];
      
      // Check if new format (4 columns) or old format (3 columns)
      const isNewFormat = newFormatHeaders.every(h => header.includes(h));
      const isOldFormat = oldFormatHeaders.every(h => header.includes(h));
      
      if (!isNewFormat && !isOldFormat) {
        // console.log('CSV fajl mora imati kolone: name, surname1, surname2, email (ili stari format: name, surname, email)');
        return;
      }

      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        
        if (isNewFormat && values.length >= 4) {
          // New format: name, surname1, surname2, email
          data.push({
            name: values[0],
            surname1: values[1],
            surname2: values[2],
            email: values[3]
          });
        } else if (isOldFormat && values.length >= 3) {
          // Old format: name, surname, email (treat surname as surname1)
          data.push({
            name: values[0],
            surname1: values[1],
            surname2: '',
            email: values[2]
          });
        }
      }
      
      setCsvPreview(data);
    };
    reader.readAsText(file);
  };

  const handleCsvImport = async () => {
    if (csvPreview.length === 0) {
      // console.log('Nema podataka za import');
      return;
    }

    setCsvImportLoading(true);
    // Track counts for potential future use (currently logged in commented console.log)
    // eslint-disable-next-line no-unused-vars
    let successCount = 0;
    // eslint-disable-next-line no-unused-vars
    let errorCount = 0;

    try {
      for (const user of csvPreview) {
        try {
          // Check if user already exists
          const existingMembers = await getDocs(
            query(collection(db, 'members'), where('email', '==', user.email))
          );

          if (!existingMembers.empty) {
            // console.log(`Korisnik sa email ${user.email} već postoji`);
            // eslint-disable-next-line no-unused-vars
            errorCount++;
            continue;
          }

          // Add new member
          await addDoc(collection(db, 'members'), {
            name: user.name || '',
            surname1: user.surname1 || '',
            surname2: user.surname2 || '',
            email: user.email,
            isAdmin: false,
            nivo: 'Osnovni',
            avatarUrl: '',
            joinedDate: new Date().toISOString(),
            totalPoints: 0
          });

          // eslint-disable-next-line no-unused-vars
          successCount++;
        } catch (error) {
          console.error(`Error adding user ${user.email}:`, error);
          // eslint-disable-next-line no-unused-vars
          errorCount++;
        }
      }

      // console.log(`Import završen! Uspešno dodano: ${successCount}, Grešaka: ${errorCount}`);
      
      // Reset form and reload members
      setCsvPreview([]);
      setCsvImportModal(false);
      loadMembers();
      onRefresh();
    } catch (error) {
      console.error('Error during CSV import:', error);
      // console.log('Greška prilikom importa CSV fajla');
    } finally {
      setCsvImportLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMember.name || !newMember.email) {
      // console.log('Popunite sva obavezna polja (ime i email)');
      return;
    }

    // Validate three-field name
    const validation = validateAndFormatThreeFieldName(newMember.name, newMember.surname1, newMember.surname2);
    if (!validation.isValid) {
      // console.log(validation.errors.join(', '));
      return;
    }

    setLoading(true);
    try {
      let avatarUrl = '';
      
      // Upload avatar if provided
      if (avatarFile) {
        const avatarRef = ref(storage, `avatars/${Date.now()}_${avatarFile.name}`);
        await uploadBytes(avatarRef, avatarFile);
        avatarUrl = await getDownloadURL(avatarRef);
      }

      await addDoc(collection(db, 'members'), {
        name: validation.name,
        surname1: validation.surname1,
        surname2: validation.surname2,
        email: newMember.email.trim().toLowerCase(), // Normalize email
        isAdmin: newMember.isAdmin,
        nivo: newMember.nivo || 'Osnovni',
        avatarUrl,
        joinedDate: new Date().toISOString()
      });
      
      // console.log('Član dodat!');
      setShowAddModal(false);
      setNewMember({ name: '', surname1: '', surname2: '', email: '', isAdmin: false, nivo: 'Osnovni' });
      setAvatarFile(null);
      loadMembers();
      onRefresh();
    } catch (error) {
      console.error('Error adding member:', error);
      // console.log('Greška prilikom dodavanja člana.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMember = (member) => {
    // Parse member data: if has surname1/surname2, use them; otherwise parse from name
    let parsedMember = { ...member };
    
    if (!member.surname1 && !member.surname2 && member.name) {
      // Legacy format: parse name into parts
      const parts = member.name.trim().split(/\s+/);
      if (parts.length >= 2) {
        parsedMember.name = parts[0];
        parsedMember.surname1 = parts.slice(1).join(' '); // Everything after first name
        parsedMember.surname2 = '';
      }
    }
    
    setSelectedMember(parsedMember);
    setShowEditModal(true);
  };

  const handleUpdateMember = async () => {
    if (!selectedMember.name || !selectedMember.email) {
      // console.log('Popunite sva obavezna polja (ime i email)');
      return;
    }

    // Validate three-field name
    const validation = validateAndFormatThreeFieldName(
      selectedMember.name,
      selectedMember.surname1,
      selectedMember.surname2
    );
    if (!validation.isValid) {
      // console.log(validation.errors.join(', '));
      return;
    }

    setLoading(true);
    try {
      let avatarUrl = selectedMember.avatarUrl;
      
      if (avatarFile) {
        const avatarRef = ref(storage, `avatars/${Date.now()}_${avatarFile.name}`);
        await uploadBytes(avatarRef, avatarFile);
        avatarUrl = await getDownloadURL(avatarRef);
      }

      await updateDoc(doc(db, 'members', selectedMember.id), {
        name: validation.name,
        surname1: validation.surname1,
        surname2: validation.surname2,
        email: selectedMember.email.trim().toLowerCase(), // Normalize email
        isAdmin: selectedMember.isAdmin,
        nivo: selectedMember.nivo || 'Osnovni',
        avatarUrl
      });
      
      // console.log('Član ažuriran!');
      setShowEditModal(false);
      setSelectedMember(null);
      setAvatarFile(null);
      loadMembers();
      onRefresh();
    } catch (error) {
      console.error('Error updating member:', error);
      // console.log('Greška prilikom ažuriranja člana.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovog člana i sve njegove podatke (poeni, aktivnosti, pobede)?')) return;

    try {
      const quarter = getEffectiveQuarter(settings);
      await deleteMemberAndAllData(db, memberId, quarter);
      loadMembers();
      onRefresh();
    } catch (error) {
      console.error('Error deleting member:', error);
    }
  };

  const handleSaveLevelEdits = async () => {
    const entries = Object.entries(levelEdits);
    if (entries.length === 0) {
      setIsEditingLevels(false);
      return;
    }
    setSavingLevels(true);
    try {
      await Promise.all(
        entries.map(([memberId, nivo]) =>
          updateDoc(doc(db, 'members', memberId), { nivo: nivo || 'Osnovni' })
        )
      );
      setLevelEdits({});
      setIsEditingLevels(false);
      loadMembers();
      onRefresh();
    } catch (error) {
      console.error('Error saving level updates:', error);
    } finally {
      setSavingLevels(false);
    }
  };

  const getMemberNivo = (member) => levelEdits[member.id] !== undefined ? levelEdits[member.id] : (member.nivo || 'Osnovni');

  const handleReEnableChallenge = async (activityId, memberId, points) => {
    if (!window.confirm(`Da li ste sigurni da želite da ponovo omogućite ovu mikro-akciju? Korisnik će izgubiti ${points} poena.`)) return;

    try {
      // Delete the activity
      await deleteDoc(doc(db, 'quarters', settings.currentQuarter, 'activities', activityId));

      // Subtract points from user's total
      const pointsRef = doc(db, 'quarters', settings.currentQuarter, 'points', memberId);
      const pointsDoc = await getDoc(pointsRef);
      
      if (pointsDoc.exists()) {
        const currentPoints = pointsDoc.data().totalPoints || 0;
        const newPoints = Math.max(0, currentPoints - points);
        
        await updateDoc(pointsRef, {
          totalPoints: newPoints,
          lastUpdated: new Date().toISOString()
        });
      }

      // console.log('Izazov je ponovo omogućen! Poeni su oduzeti.');
      loadChallengeActivities();
      onRefresh();
    } catch (error) {
      console.error('Error re-enabling challenge:', error);
      // console.log('Greška prilikom ponovnog omogućavanja izazova.');
    }
  };

  const handleAdjustUserPoints = async () => {
    if (!pointsAdjustment.memberId || !pointsAdjustment.adjustment) {
      // console.log('Popunite sva polja');
      return;
    }

    const adjustment = parseInt(pointsAdjustment.adjustment);
    if (isNaN(adjustment)) {
      // console.log('Unesite validan broj poena');
      return;
    }

    try {
      const pointsRef = doc(db, 'quarters', settings.currentQuarter, 'points', pointsAdjustment.memberId);
      const pointsDoc = await getDoc(pointsRef);
      
      let newPoints = adjustment;
      if (pointsDoc.exists()) {
        const currentPoints = pointsDoc.data().totalPoints || 0;
        newPoints = Math.max(0, currentPoints + adjustment);
      }

      if (pointsDoc.exists()) {
        await updateDoc(pointsRef, {
          totalPoints: newPoints,
          lastUpdated: new Date().toISOString()
        });
      } else {
        await setDoc(pointsRef, {
          memberId: pointsAdjustment.memberId,
          totalPoints: newPoints,
          lastUpdated: new Date().toISOString()
        });
      }

      // Create manual adjustment activity
      await addDoc(collection(db, 'quarters', settings.currentQuarter, 'activities'), {
        memberId: pointsAdjustment.memberId,
        memberName: members.find(m => m.id === pointsAdjustment.memberId)?.name || 'Unknown',
        type: 'manual_adjustment',
        points: adjustment,
        reason: pointsAdjustment.reason || 'Manual adjustment by admin',
        timestamp: new Date().toISOString()
      });

      // console.log(`Poeni uspešno ažurirani! ${adjustment > 0 ? '+' : ''}${adjustment} poena`);
      setShowPointsModal(false);
      setPointsAdjustment({ memberId: '', adjustment: '', reason: '' });
      loadChallengeActivities();
      onRefresh();
    } catch (error) {
      console.error('Error adjusting points:', error);
      // console.log('Greška prilikom ažuriranja poena.');
    }
  };

  const filterActivities = () => {
    // console.log('🔍 Filtering activities:', {
    //   totalActivities: challengeActivities.length,
    //   searchTerm,
    //   selectedMemberId,
    //   selectedChallengeId
    // });
    
    const filtered = challengeActivities.filter(activity => {
      const matchesSearch = !searchTerm || 
        activity.memberName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.challengeTitle.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesMember = !selectedMemberId || activity.memberId === selectedMemberId;
      const matchesChallenge = !selectedChallengeId || activity.challengeId === selectedChallengeId;
      
      return matchesSearch && matchesMember && matchesChallenge;
    });
    
    // console.log('✅ Filtered activities:', filtered.length);
    return filtered;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 lg:mb-6 gap-4">
        <h2 className="text-xl lg:text-2xl font-semibold text-gray-800">Upravljanje Članovima ({members.length})</h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={() => setCsvImportModal(true)}
            className="bg-blue-600 text-white px-4 lg:px-6 py-2 lg:py-3 rounded-lg hover:bg-blue-700 transition font-medium flex items-center gap-2 text-sm lg:text-base w-full sm:w-auto justify-center"
          >
            <svg className="w-4 h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Import CSV
          </button>
          {isEditingLevels ? (
            <>
              <button
                onClick={() => { setIsEditingLevels(false); setLevelEdits({}); }}
                className="border border-gray-300 text-gray-700 px-4 lg:px-6 py-2 lg:py-3 rounded-lg hover:bg-gray-50 transition font-medium text-sm lg:text-base"
              >
                Otkaži
              </button>
              <button
                onClick={handleSaveLevelEdits}
                disabled={savingLevels || Object.keys(levelEdits).length === 0}
                className="bg-[#5c614d] text-white px-4 lg:px-6 py-2 lg:py-3 rounded-lg hover:bg-[#5c614d]/90 transition font-medium flex items-center gap-2 text-sm lg:text-base disabled:opacity-50"
              >
                {savingLevels ? 'Čuvanje...' : 'Sačuvaj'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditingLevels(true)}
              className="border border-[#931b1d] text-[#931b1d] px-4 lg:px-6 py-2 lg:py-3 rounded-lg hover:bg-[#931b1d]/10 transition font-medium flex items-center gap-2 text-sm lg:text-base w-full sm:w-auto justify-center"
            >
              <svg className="w-4 h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Uredi Nivoe
            </button>
          )}
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-[#931b1d] text-white px-4 lg:px-6 py-2 lg:py-3 rounded-lg hover:bg-[#931b1d] transition font-medium flex items-center gap-2 text-sm lg:text-base w-full sm:w-auto justify-center"
          >
            <svg className="w-4 h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Dodaj Člana
          </button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block lg:hidden space-y-4">
        {members.map(member => (
          <div key={member.id} className="bg-white rounded-xl shadow p-4">
            <div className="flex items-center gap-3 mb-3">
              {member.avatarUrl ? (
                <img src={member.avatarUrl} alt={getDisplayName(member)} className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#931b1d]/100 flex items-center justify-center text-white font-bold">
                  {(() => {
                    const displayName = getDisplayName(member);
                    return displayName ? displayName.split(' ').map(n => n[0]).join('') : '?';
                  })()}
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800">{getDisplayName(member)}</h3>
                <p className="text-sm text-gray-600">{member.email}</p>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500 flex items-center gap-2">
                {isEditingLevels ? (
                  <span className="flex items-center gap-1">
                    <select
                      value={getMemberNivo(member)}
                      onChange={(e) => setLevelEdits(prev => ({ ...prev, [member.id]: e.target.value }))}
                      className="text-gray-700 border border-gray-300 rounded-lg py-1 pl-2 pr-6 text-sm focus:ring-2 focus:ring-[#931b1d] focus:border-transparent bg-white appearance-none cursor-pointer"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23374151'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.25rem center', backgroundSize: '1rem' }}
                    >
                      {NIVO_OPTIONS.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </span>
                ) : (
                  <span className="text-gray-700">{member.nivo || 'Osnovni'}</span>
                )}
                {member.isAdmin && <span className="bg-[#931b1d]/20 text-[#931b1d] px-2 py-1 rounded-full text-xs">Admin</span>}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEditMember(member)}
                  className="text-[#5c614d] hover:text-[#5c614d] text-sm"
                >
                  Uredi
                </button>
                <button
                  onClick={() => handleDeleteMember(member.id)}
                  className="text-[#931b1d] hover:text-[#931b1d] text-sm"
                >
                  Obriši
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Član</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Nivo</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Datum Pridruživanja</th>
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Akcije</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {members.map(member => (
              <tr key={member.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {member.avatarUrl ? (
                      <img src={member.avatarUrl} alt={getDisplayName(member)} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#931b1d]/30 flex items-center justify-center text-[#931b1d] font-semibold">
                        {(() => {
                          const displayName = getDisplayName(member);
                          return displayName ? displayName.split(' ').map(n => n[0]).join('') : '?';
                        })()}
                      </div>
                    )}
                    <span className="font-medium text-gray-800">{getDisplayName(member)}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-600">{member.email}</td>
                <td className="px-6 py-4">
                  {isEditingLevels ? (
                    <span className="flex items-center gap-1">
                      <select
                        value={getMemberNivo(member)}
                        onChange={(e) => setLevelEdits(prev => ({ ...prev, [member.id]: e.target.value }))}
                        className="text-gray-700 border border-gray-300 rounded-lg py-1.5 pl-2 pr-8 text-sm focus:ring-2 focus:ring-[#931b1d] focus:border-transparent bg-white appearance-none cursor-pointer"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23374151'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.25rem center', backgroundSize: '1rem' }}
                      >
                        {NIVO_OPTIONS.map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </span>
                  ) : (
                    <span className="text-sm text-gray-700">{member.nivo || 'Osnovni'}</span>
                  )}
                  {member.isAdmin && (
                    <span className="ml-2 bg-[#931b1d]/20 text-[#931b1d] text-xs px-2 py-1 rounded-full font-medium">
                      Admin
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-gray-600 text-sm">
                  {new Date(member.joinedDate).toLocaleDateString('sr-RS')}
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => {
                      setSelectedMember(member);
                      setShowEditModal(true);
                    }}
                    className="text-[#5c614d] hover:text-[#5c614d] font-medium mr-4"
                  >
                    Uredi
                  </button>
                  <button
                    onClick={() => handleDeleteMember(member.id)}
                    className="text-[#931b1d] hover:text-[#931b1d] font-medium"
                  >
                    Obriši
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-start justify-between gap-4 mb-4 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800">Dodaj Novog Člana</h3>
              <button type="button" onClick={() => { setShowAddModal(false); setNewMember({ name: '', surname1: '', surname2: '', email: '', isAdmin: false, nivo: 'Osnovni' }); setAvatarFile(null); }} className="p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition flex-shrink-0" aria-label="Zatvori">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="space-y-4 mb-6 overflow-y-auto min-h-0 flex-1 pr-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ime *</label>
                <input
                  type="text"
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="Ana"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prezime 1</label>
                <input
                  type="text"
                  value={newMember.surname1}
                  onChange={(e) => setNewMember({ ...newMember, surname1: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="Marković"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prezime 2</label>
                <input
                  type="text"
                  value={newMember.surname2}
                  onChange={(e) => setNewMember({ ...newMember, surname2: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="Petrović"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="ana.markovic@example.com"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email će biti korišćen za prijavu u sistem
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Avatar (opciono)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setAvatarFile(e.target.files[0])}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isAdmin"
                  checked={newMember.isAdmin}
                  onChange={(e) => setNewMember({ ...newMember, isAdmin: e.target.checked })}
                  className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                />
                <label htmlFor="isAdmin" className="text-sm text-gray-700">Admin privilegije</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nivo</label>
                <select
                  value={newMember.nivo || 'Osnovni'}
                  onChange={(e) => setNewMember({ ...newMember, nivo: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                >
                  {NIVO_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewMember({ name: '', surname1: '', surname2: '', email: '', isAdmin: false, nivo: 'Osnovni' });
                  setAvatarFile(null);
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Zatvori
              </button>
              <button
                onClick={handleAddMember}
                disabled={loading}
                className="flex-1 bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d] transition disabled:opacity-50"
              >
                {loading ? 'Dodavanje...' : 'Dodaj'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {showEditModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md relative">
            <button type="button" onClick={() => { setShowEditModal(false); setSelectedMember(null); }} className="absolute top-4 right-4 p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition" aria-label="Zatvori">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 pr-8">Uredi Člana</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ime *</label>
                <input
                  type="text"
                  value={selectedMember.name || ''}
                  onChange={(e) => setSelectedMember({ ...selectedMember, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prezime 1</label>
                <input
                  type="text"
                  value={selectedMember.surname1 || ''}
                  onChange={(e) => setSelectedMember({ ...selectedMember, surname1: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="Marković"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prezime 2</label>
                <input
                  type="text"
                  value={selectedMember.surname2 || ''}
                  onChange={(e) => setSelectedMember({ ...selectedMember, surname2: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="Petrović"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={selectedMember.email}
                  onChange={(e) => setSelectedMember({ ...selectedMember, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email će biti korišćen za prijavu u sistem
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Novi Avatar (opciono)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setAvatarFile(e.target.files[0])}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isAdminEdit"
                  checked={selectedMember.isAdmin}
                  onChange={(e) => setSelectedMember({ ...selectedMember, isAdmin: e.target.checked })}
                  className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                />
                <label htmlFor="isAdminEdit" className="text-sm text-gray-700">Admin privilegije</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nivo</label>
                <select
                  value={selectedMember.nivo || 'Osnovni'}
                  onChange={(e) => setSelectedMember({ ...selectedMember, nivo: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                >
                  {NIVO_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedMember(null);
                  setAvatarFile(null);
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Zatvori
              </button>
              <button
                onClick={handleUpdateMember}
                disabled={loading}
                className="flex-1 bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d] transition disabled:opacity-50"
              >
                {loading ? 'Čuvanje...' : 'Sačuvaj'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Challenge Activities Tracking Section */}
      <div className="mt-8 lg:mt-12">
        <div className="border-t border-gray-200 pt-6 lg:pt-8">
          <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-4 lg:mb-6">Praćenje mikro-akcija</h3>
          
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-3 lg:p-4 mb-4 lg:mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pretraži</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Ime člana ili naziv mikro-akcija..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Član</label>
              <select
                  value={selectedMemberId}
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                >
                  <option value="">Svi članovi</option>
                  {members.map(member => (
                    <option key={member.id} value={member.id}>{member.name}</option>
                  ))}
              </select>
            </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mikro-akcija</label>
                <select
                  value={selectedChallengeId}
                  onChange={(e) => setSelectedChallengeId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                >
                  <option value="">Sve mikro-akcije</option>
                  {challenges.map(challenge => (
                    <option key={challenge.id} value={challenge.id}>{challenge.title}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end sm:col-span-2 lg:col-span-1">
            <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedMemberId('');
                    setSelectedChallengeId('');
                  }}
                  className="w-full bg-gray-200 text-gray-700 px-3 lg:px-4 py-2 rounded-lg hover:bg-gray-300 transition text-sm lg:text-base"
                >
                  Resetuj filtere
            </button>
          </div>
            </div>
          </div>

          {/* Debug Information */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-yellow-800 mb-2">Debug Information:</h4>
            <div className="text-xs text-yellow-700 space-y-1">
              <div>Current Quarter: {settings?.currentQuarter || 'N/A'}</div>
              <div>Total Challenge Activities: {challengeActivities.length}</div>
              <div>Total Challenges: {challenges.length}</div>
              <div>Total Members: {members.length}</div>
              <div>Filtered Activities: {filterActivities().length}</div>
            </div>
          </div>

          {/* Challenge Activities Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto -mx-3 lg:mx-0">
              <div className="inline-block min-w-full align-middle">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Član</th>
                      <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Mikro-akcija</th>
                      <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Poeni</th>
                      <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Dokaz</th>
                      <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Datum</th>
                      <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Akcije</th>
                    </tr>
                  </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filterActivities().length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-3 lg:px-6 py-4 text-center text-gray-500 text-xs lg:text-sm">
                        {challengeActivities.length === 0 
                          ? `Nema završenih mikro-akcija u bazi podataka za kvartal ${settings?.currentQuarter || 'N/A'}. Da biste videli mikro-akcije, korisnici moraju prvo da završe neke mikro-akcije.`
                          : 'Nema pronađenih mikro-akcija koji odgovaraju filterima'
                        }
                      </td>
                    </tr>
                  ) : (
                    filterActivities().map(activity => (
                      <tr key={activity.id} className="hover:bg-gray-50">
                        <td className="px-3 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm font-medium text-gray-900">
                          {activity.memberName}
                        </td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-xs lg:text-sm text-gray-900 max-w-[150px] lg:max-w-none truncate">
                          {activity.challengeTitle}
                        </td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-900">
                          <span className="text-[#5c614d] font-medium">+{activity.points}</span>
                        </td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-900">
                          {activity.proofUrl ? (
                            <a 
                              href={activity.proofUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline truncate block max-w-[200px]"
                              title={activity.proofUrl}
                            >
                              Pregledaj dokaz
                            </a>
                          ) : (
                            <span className="text-gray-400 italic">Bez dokaza</span>
                          )}
                        </td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-500">
                          <div className="flex flex-col lg:flex-row lg:gap-1">
                            <span>{new Date(activity.timestamp).toLocaleDateString('sr-RS')}</span>
                            <span className="hidden lg:inline">{new Date(activity.timestamp).toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm font-medium">
                          <div className="flex flex-col lg:flex-row gap-1 lg:gap-2">
                            <button
                              onClick={() => handleReEnableChallenge(activity.id, activity.memberId, activity.points)}
                              className="text-[#931b1d] hover:text-[#931b1d] text-left"
                            >
                              Ponovo omogući
                            </button>
                            <button
                              onClick={() => {
                                setPointsAdjustment({ 
                                  memberId: activity.memberId, 
                                  adjustment: '', 
                                  reason: `Adjustment for ${activity.challengeTitle}` 
                                });
                                setShowPointsModal(true);
                              }}
                              className="text-[#5c614d] hover:text-[#5c614d] text-left"
                            >
                              ±Poeni
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Points Adjustment Modal */}
      {showPointsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md relative">
            <button type="button" onClick={() => setShowPointsModal(false)} className="absolute top-4 right-4 p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition" aria-label="Zatvori">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 pr-8">Ažuriranje Poena</h3>
            <div className="space-y-4 mb-6">
          <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Član</label>
                <select
                  value={pointsAdjustment.memberId}
                  onChange={(e) => setPointsAdjustment({ ...pointsAdjustment, memberId: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                >
                  <option value="">Izaberite člana</option>
                  {members.map(member => (
                    <option key={member.id} value={member.id}>{member.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Promena poena</label>
                <input
                  type="number"
                  value={pointsAdjustment.adjustment}
                  onChange={(e) => setPointsAdjustment({ ...pointsAdjustment, adjustment: e.target.value })}
                  placeholder="+10 ili -5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Pozitivni broj za dodavanje, negativni za oduzimanje</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Razlog (opciono)</label>
                <textarea
                  value={pointsAdjustment.reason}
                  onChange={(e) => setPointsAdjustment({ ...pointsAdjustment, reason: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  rows="3"
                  placeholder="Razlog za promenu poena..."
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPointsModal(false);
                  setPointsAdjustment({ memberId: '', adjustment: '', reason: '' });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Zatvori
              </button>
              <button
                onClick={handleAdjustUserPoints}
                className="flex-1 bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d] transition"
              >
                Ažuriraj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {csvImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
            <button type="button" onClick={() => setCsvImportModal(false)} className="absolute top-4 right-4 p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition z-10 bg-white" aria-label="Zatvori">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 pr-8">Import Članova iz CSV Fajla</h3>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Odaberite CSV fajl
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                CSV fajl mora imati kolone: name, surname1, surname2, email (ili stari format: name, surname, email)
              </p>
            </div>

            {csvPreview.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">
                  Pregled podataka ({csvPreview.length} korisnika)
                </h4>
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Ime</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Prezime 1</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Prezime 2</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Email</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Puno Ime</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.map((user, index) => (
                        <tr key={index} className="border-b border-gray-100">
                          <td className="px-3 py-2">{user.name}</td>
                          <td className="px-3 py-2">{user.surname1 || ''}</td>
                          <td className="px-3 py-2">{user.surname2 || ''}</td>
                          <td className="px-3 py-2">{user.email}</td>
                          <td className="px-3 py-2 font-medium">{[user.name, user.surname1, user.surname2].filter(Boolean).join(' ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex gap-3">
            <button
                onClick={() => {
                  setCsvImportModal(false);
                  setCsvPreview([]);
                }}
                disabled={csvImportLoading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Zatvori
              </button>
              <button
                onClick={handleCsvImport}
                disabled={csvImportLoading || csvPreview.length === 0}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {csvImportLoading ? 'Importovanje...' : `Import ${csvPreview.length} korisnika`}
            </button>
            </div>
          </div>
          </div>
        )}
      </div>
  );
}

// Challenges Management Component
const CHALLENGE_TAGS = [
  'B2C | Digitalni proizvod',
  'B2C | Fizički proizvod',
  'B2B',
  'Ponuda i prezentacija ponude',
  'Marketing i prodaja',
  'Optimizacija i sistemi'
];

const CHALLENGE_LEVELS = ['Osnovni', 'Napredni', 'Mastermind'];

function getChallengeLevels(challenge) {
  if (challenge.levels && challenge.levels.length > 0) return challenge.levels;
  return [challenge.level || 'Osnovni'];
}

function getChallengesForLevel(challenges, level) {
  return challenges.filter((c) => getChallengeLevels(c).includes(level));
}

function AdminChallenges({ db }) {
  const [challenges, setChallenges] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [viewMode, setViewMode] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [newChallenge, setNewChallenge] = useState({
    title: '',
    description: '',
    points: 10,
    isActive: true,
    isRecurring: false,
    requiresSubmission: false,
    deadline: '',
    levels: ['Osnovni'],
    tags: [],
    materialLink: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadChallenges();
  }, []);

  const loadChallenges = async () => {
    try {
      const challengesSnapshot = await getDocs(collection(db, 'challenges'));
      setChallenges(challengesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading challenges:', error);
    }
  };

  const handleAddChallenge = async () => {
    if (!newChallenge.title || !newChallenge.description) {
      return;
    }
    const levels = (newChallenge.levels && newChallenge.levels.length > 0)
      ? newChallenge.levels
      : ['Osnovni'];

    setLoading(true);
    try {
      const challengeData = {
        ...newChallenge,
        levels,
        points: parseInt(newChallenge.points),
        deadline: newChallenge.deadline ? new Date(newChallenge.deadline).toISOString() : null,
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'challenges'), challengeData);

      setShowAddModal(false);
      setNewChallenge({
        title: '',
        description: '',
        points: 10,
        isActive: true,
        isRecurring: false,
        requiresSubmission: false,
        deadline: '',
        levels: ['Osnovni'],
        tags: [],
        materialLink: ''
      });
      loadChallenges();
    } catch (error) {
      console.error('Error adding challenge:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateChallenge = async () => {
    if (!selectedChallenge.title || !selectedChallenge.description) {
      return;
    }
    const levels = (selectedChallenge.levels && selectedChallenge.levels.length > 0)
      ? selectedChallenge.levels
      : [selectedChallenge.level || 'Osnovni'];

    setLoading(true);
    try {
      const challengeData = {
        ...selectedChallenge,
        levels,
        points: parseInt(selectedChallenge.points),
        deadline: selectedChallenge.deadline ? new Date(selectedChallenge.deadline).toISOString() : null
      };

      await updateDoc(doc(db, 'challenges', selectedChallenge.id), challengeData);

      setShowEditModal(false);
      setSelectedChallenge(null);
      loadChallenges();
    } catch (error) {
      console.error('Error updating challenge:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovu mikro-akciju?')) return;

    try {
      await deleteDoc(doc(db, 'challenges', challengeId));
      // console.log('Izazov obrisan!');
      loadChallenges();
    } catch (error) {
      console.error('Error deleting challenge:', error);
      // console.log('Greška prilikom brisanja izazova.');
    }
  };

          return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Upravljanje mikro-akcijama ({challenges.length})</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('all')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition ${viewMode === 'all' ? 'bg-[#931b1d] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Sve
          </button>
          <button
            onClick={() => setViewMode('byLevel')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition ${viewMode === 'byLevel' ? 'bg-[#931b1d] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Po nivou
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-[#931b1d] text-white px-6 py-3 rounded-lg hover:bg-[#931b1d] transition font-medium flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Dodaj mikro-akciju
          </button>
        </div>
      </div>

      {viewMode === 'all' && (
        <div className="grid gap-4">
          {challenges.map(challenge => (
            <div key={challenge.id} className="bg-white rounded-xl shadow p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-800">{challenge.title}</h3>
                    {challenge.isActive && (
                      <span className="bg-[#5c614d]/20 text-[#5c614d] text-xs px-2 py-1 rounded-full font-medium">
                        Aktivan
                      </span>
                    )}
                    {challenge.isRecurring && (
                      <span className="bg-[#5c614d]/20 text-[#5c614d] text-xs px-2 py-1 rounded-full font-medium">
                        Ponavljajući
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{challenge.description?.replace(/<[^>]+>/g, '') || 'Bez opisa'}</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {getChallengeLevels(challenge).length === CHALLENGE_LEVELS.length ? (
                      <span className="text-xs px-2 py-1 rounded-full font-medium bg-gray-200 text-gray-700">Svi nivoi</span>
                    ) : (
                      getChallengeLevels(challenge).map((lvl) => (
                        <span key={lvl} className="text-xs px-2 py-1 rounded-full font-medium bg-gray-200 text-gray-700">{lvl}</span>
                      ))
                    )}
                    {(challenge.tags || []).map((tag) => (
                      <span key={`adm-tag-${challenge.id}-${tag}`} className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: '#931b1d20', color: '#931b1d' }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-4 text-sm text-gray-500">
                    <span>{challenge.points} poena</span>
                    {challenge.deadline && (
                      <span>Rok: {new Date(challenge.deadline).toLocaleDateString('sr-RS')}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => {
                      setSelectedChallenge({
                        ...challenge,
                        deadline: challenge.deadline ? new Date(challenge.deadline).toISOString().split('T')[0] : '',
                        levels: challenge.levels && challenge.levels.length ? challenge.levels : (challenge.level ? [challenge.level] : ['Osnovni']),
                        tags: challenge.tags || [],
                        materialLink: challenge.materialLink || ''
                      });
                      setShowEditModal(true);
                    }}
                    className="text-[#5c614d] hover:text-[#5c614d] font-medium"
                  >
                    Uredi
                  </button>
                  <button
                    onClick={() => handleDeleteChallenge(challenge.id)}
                    className="text-[#931b1d] hover:text-[#931b1d] font-medium"
                  >
                    Obriši
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewMode === 'byLevel' && (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <label htmlFor="level-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">Prikaži nivo:</label>
            <select
              id="level-filter"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent text-sm bg-white"
            >
              <option value="all">Svi nivoi</option>
              {CHALLENGE_LEVELS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          {CHALLENGE_LEVELS.filter((level) => levelFilter === 'all' || level === levelFilter).map((level) => {
            const levelChallenges = getChallengesForLevel(challenges, level);
            return (
              <div key={level}>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-2">
                  {level} ({levelChallenges.length})
                </h3>
                <div className="grid gap-4">
                  {levelChallenges.length === 0 ? (
                    <p className="text-gray-500 text-sm">Nema mikro-akcija za ovaj nivo.</p>
                  ) : (
                    levelChallenges.map((challenge) => (
                      <div key={challenge.id} className="bg-white rounded-xl shadow p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-lg font-semibold text-gray-800">{challenge.title}</h3>
                              {challenge.isActive && (
                                <span className="bg-[#5c614d]/20 text-[#5c614d] text-xs px-2 py-1 rounded-full font-medium">
                                  Aktivan
                                </span>
                              )}
                              {challenge.isRecurring && (
                                <span className="bg-[#5c614d]/20 text-[#5c614d] text-xs px-2 py-1 rounded-full font-medium">
                                  Ponavljajući
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600 text-sm mb-2">{challenge.description?.replace(/<[^>]+>/g, '') || 'Bez opisa'}</p>
                            <div className="flex flex-wrap gap-2 mb-2">
                              {getChallengeLevels(challenge).length === CHALLENGE_LEVELS.length ? (
                                <span className="text-xs px-2 py-1 rounded-full font-medium bg-gray-200 text-gray-700">Svi nivoi</span>
                              ) : (
                                getChallengeLevels(challenge).map((lvl) => (
                                  <span key={lvl} className="text-xs px-2 py-1 rounded-full font-medium bg-gray-200 text-gray-700">{lvl}</span>
                                ))
                              )}
                              {(challenge.tags || []).map((tag) => (
                                <span key={`bl-${level}-${challenge.id}-${tag}`} className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: '#931b1d20', color: '#931b1d' }}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                            <div className="flex gap-4 text-sm text-gray-500">
                              <span>{challenge.points} poena</span>
                              {challenge.deadline && (
                                <span>Rok: {new Date(challenge.deadline).toLocaleDateString('sr-RS')}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => {
                                setSelectedChallenge({
                                  ...challenge,
                                  deadline: challenge.deadline ? new Date(challenge.deadline).toISOString().split('T')[0] : '',
                                  levels: challenge.levels && challenge.levels.length ? challenge.levels : (challenge.level ? [challenge.level] : ['Osnovni']),
                                  tags: challenge.tags || [],
                                  materialLink: challenge.materialLink || ''
                                });
                                setShowEditModal(true);
                              }}
                              className="text-[#5c614d] hover:text-[#5c614d] font-medium"
                            >
                              Uredi
                            </button>
                            <button
                              onClick={() => handleDeleteChallenge(challenge.id)}
                              className="text-[#931b1d] hover:text-[#931b1d] font-medium"
                            >
                              Obriši
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Challenge Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto relative">
            <button type="button" onClick={() => { setShowAddModal(false); setNewChallenge({ title: '', description: '', points: 10, isActive: true, isRecurring: false, requiresSubmission: false, deadline: '', levels: ['Osnovni'], tags: [], materialLink: '' }); }} className="absolute top-4 right-4 p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition z-10 bg-white" aria-label="Zatvori">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 pr-8">Dodaj novu mikro-akciju</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Naslov *</label>
                <input
                  type="text"
                  value={newChallenge.title}
                  onChange={(e) => setNewChallenge({ ...newChallenge, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="Napiši 5 blog postova"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Opis *</label>
                <RichTextEditor
                  value={newChallenge.description}
                  onChange={(html) => setNewChallenge({ ...newChallenge, description: html })}
                  placeholder="Kreiraj 5 SEO optimizovanih blog postova za svoj biznis"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dodatni materijali</label>
                <input
                  type="url"
                  value={newChallenge.materialLink || ''}
                  onChange={(e) => setNewChallenge({ ...newChallenge, materialLink: e.target.value.trim() })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Link će se otvoriti u novoj kartici kada korisnik klikne na „Dodatni materijali”.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Poeni</label>
                <input
                  type="number"
                  value={newChallenge.points}
                  onChange={(e) => setNewChallenge({ ...newChallenge, points: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rok (opciono)</label>
                <input
                  type="date"
                  value={newChallenge.deadline}
                  onChange={(e) => setNewChallenge({ ...newChallenge, deadline: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={newChallenge.isActive}
                    onChange={(e) => setNewChallenge({ ...newChallenge, isActive: e.target.checked })}
                    className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">Aktivan</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isRecurring"
                    checked={newChallenge.isRecurring}
                    onChange={(e) => setNewChallenge({ ...newChallenge, isRecurring: e.target.checked })}
                    className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                  />
                  <label htmlFor="isRecurring" className="text-sm text-gray-700">Ponavljajući</label>
                </div>
              </div>

              {/* Nivoi (može više ili svi) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nivoi</label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={(newChallenge.levels || []).length === CHALLENGE_LEVELS.length}
                      onChange={(e) => setNewChallenge({
                        ...newChallenge,
                        levels: e.target.checked ? [...CHALLENGE_LEVELS] : (newChallenge.levels || ['Osnovni']).length === CHALLENGE_LEVELS.length ? ['Osnovni'] : (newChallenge.levels || [])
                      })}
                      className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                    />
                    Svi nivoi
                  </label>
                  {CHALLENGE_LEVELS.map((n) => (
                    <label key={n} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={(newChallenge.levels || []).includes(n)}
                        disabled={(newChallenge.levels || []).length === CHALLENGE_LEVELS.length}
                        onChange={(e) => {
                          const current = newChallenge.levels || ['Osnovni'];
                          const next = e.target.checked
                            ? [...current, n]
                            : current.filter((x) => x !== n);
                          setNewChallenge({ ...newChallenge, levels: next.length ? next : ['Osnovni'] });
                        }}
                        className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                      />
                      {n}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">Izaberite nivoe kojima je mikro-akcija dostupna (može više ili svi).</p>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tagovi (može više)</label>
                <div className="grid grid-cols-1 gap-2">
                  {CHALLENGE_TAGS.map((tag) => (
                    <label key={tag} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={(newChallenge.tags || []).includes(tag)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setNewChallenge({
                            ...newChallenge,
                            tags: checked
                              ? [...(newChallenge.tags || []), tag]
                              : (newChallenge.tags || []).filter((v) => v !== tag)
                          });
                        }}
                        className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                      />
                      {tag}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewChallenge({
                    title: '',
                    description: '',
                    points: 10,
                    isActive: true,
                    isRecurring: false,
                    requiresSubmission: false,
                    deadline: '',
                    levels: ['Osnovni'],
                    tags: [],
                    materialLink: ''
                  });
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Zatvori
              </button>
              <button
                onClick={handleAddChallenge}
                disabled={loading}
                className="flex-1 bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d] transition disabled:opacity-50"
              >
                {loading ? 'Dodavanje...' : 'Dodaj'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Challenge Modal */}
      {showEditModal && selectedChallenge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto relative">
            <button type="button" onClick={() => { setShowEditModal(false); setSelectedChallenge(null); }} className="absolute top-4 right-4 p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition z-10 bg-white" aria-label="Zatvori">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 pr-8">Uredi mikro-akciju</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Naslov *</label>
                <input
                  type="text"
                  value={selectedChallenge.title}
                  onChange={(e) => setSelectedChallenge({ ...selectedChallenge, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Opis *</label>
                <RichTextEditor
                  value={selectedChallenge.description || ''}
                  onChange={(html) => setSelectedChallenge({ ...selectedChallenge, description: html })}
                  placeholder="Opis mikro-akcije"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dodatni materijali</label>
                <input
                  type="url"
                  value={selectedChallenge.materialLink || ''}
                  onChange={(e) => setSelectedChallenge({ ...selectedChallenge, materialLink: e.target.value.trim() })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Poeni</label>
                <input
                  type="number"
                  value={selectedChallenge.points}
                  onChange={(e) => setSelectedChallenge({ ...selectedChallenge, points: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rok (opciono)</label>
                <input
                  type="date"
                  value={selectedChallenge.deadline}
                  onChange={(e) => setSelectedChallenge({ ...selectedChallenge, deadline: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActiveEdit"
                    checked={selectedChallenge.isActive}
                    onChange={(e) => setSelectedChallenge({ ...selectedChallenge, isActive: e.target.checked })}
                    className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                  />
                  <label htmlFor="isActiveEdit" className="text-sm text-gray-700">Aktivan</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isRecurringEdit"
                    checked={selectedChallenge.isRecurring}
                    onChange={(e) => setSelectedChallenge({ ...selectedChallenge, isRecurring: e.target.checked })}
                    className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                  />
                  <label htmlFor="isRecurringEdit" className="text-sm text-gray-700">Ponavljajući</label>
                </div>
              </div>

              {/* Nivoi (može više ili svi) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nivoi</label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={(selectedChallenge.levels || []).length === CHALLENGE_LEVELS.length}
                      onChange={(e) => setSelectedChallenge({
                        ...selectedChallenge,
                        levels: e.target.checked ? [...CHALLENGE_LEVELS] : (selectedChallenge.levels || []).length === CHALLENGE_LEVELS.length ? ['Osnovni'] : (selectedChallenge.levels || [])
                      })}
                      className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                    />
                    Svi nivoi
                  </label>
                  {CHALLENGE_LEVELS.map((n) => (
                    <label key={n} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={(selectedChallenge.levels || []).includes(n)}
                        disabled={(selectedChallenge.levels || []).length === CHALLENGE_LEVELS.length}
                        onChange={(e) => {
                          const current = selectedChallenge.levels || ['Osnovni'];
                          const next = e.target.checked
                            ? [...current, n]
                            : current.filter((x) => x !== n);
                          setSelectedChallenge({ ...selectedChallenge, levels: next.length ? next : ['Osnovni'] });
                        }}
                        className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                      />
                      {n}
                    </label>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tagovi (može više)</label>
                <div className="grid grid-cols-1 gap-2">
                  {CHALLENGE_TAGS.map((tag) => (
                    <label key={tag} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={(selectedChallenge.tags || []).includes(tag)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const current = selectedChallenge.tags || [];
                          setSelectedChallenge({
                            ...selectedChallenge,
                            tags: checked
                              ? [...current, tag]
                              : current.filter((v) => v !== tag)
                          });
                        }}
                        className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                      />
                      {tag}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedChallenge(null);
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Zatvori
              </button>
              <button
                onClick={handleUpdateChallenge}
                disabled={loading}
                className="flex-1 bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d] transition disabled:opacity-50"
              >
                {loading ? 'Čuvanje...' : 'Sačuvaj'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Events Management Component
function AdminEvents({ db }) {
  const [events, setEvents] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    points: 20,
    eventCode: '',
    isActive: true,
    date: '',
    time: '',
    zoomLink: '',
    eventLevels: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      setEvents(eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const generateEventCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleAddEvent = async () => {
    if (!newEvent.title || !newEvent.description || !newEvent.eventCode) {
      // console.log('Popunite sva obavezna polja');
      return;
    }

    setLoading(true);
    try {
      const eventData = {
        ...newEvent,
        points: parseInt(newEvent.points),
        date: newEvent.date ? new Date(newEvent.date).toISOString() : null,
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'events'), eventData);
      
      // console.log('Događaj dodat!');
      setShowAddModal(false);
      setNewEvent({
        title: '',
        description: '',
        points: 20,
        eventCode: '',
        isActive: true,
        date: '',
        time: '',
        zoomLink: '',
        eventLevels: []
      });
      loadEvents();
    } catch (error) {
      console.error('Error adding event:', error);
      // console.log('Greška prilikom dodavanja događaja.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEvent = async () => {
    if (!selectedEvent.title || !selectedEvent.description || !selectedEvent.eventCode) {
      // console.log('Popunite sva obavezna polja');
      return;
    }

    setLoading(true);
    try {
      const eventData = {
        ...selectedEvent,
        points: parseInt(selectedEvent.points),
        date: selectedEvent.date ? new Date(selectedEvent.date).toISOString() : null
      };

      await updateDoc(doc(db, 'events', selectedEvent.id), eventData);
      
      // console.log('Događaj ažuriran!');
      setShowEditModal(false);
      setSelectedEvent(null);
      loadEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      // console.log('Greška prilikom ažuriranja događaja.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj događaj?')) return;

    try {
      await deleteDoc(doc(db, 'events', eventId));
      // console.log('Događaj obrisan!');
      loadEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      // console.log('Greška prilikom brisanja događaja.');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Upravljanje Događajima ({events.length})</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-[#931b1d] text-white px-6 py-3 rounded-lg hover:bg-[#931b1d] transition font-medium flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Dodaj Događaj
        </button>
      </div>

      <div className="grid gap-4">
        {events.map(event => (
          <div key={event.id} className="bg-white rounded-xl shadow p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-800">{event.title}</h3>
                  {event.isActive && (
                    <span className="bg-[#5c614d]/20 text-[#5c614d] text-xs px-2 py-1 rounded-full font-medium">
                      Aktivan
                    </span>
                  )}
                </div>
                <p className="text-gray-600 text-sm mb-2">{event.description}</p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-3">
                  <span>{event.points} poena</span>
                  <span className="font-mono bg-gray-100 px-2 py-1 rounded">Kod: {event.eventCode}</span>
                  {event.date && (
                    <span>Datum: {new Date(event.date).toLocaleDateString('sr-RS')}</span>
                  )}
                  {event.time && (
                    <span>Vreme: {event.time}</span>
                  )}
                  {event.zoomLink && (
                    <a href={event.zoomLink} target="_blank" rel="noopener noreferrer" className="text-[#5c614d] hover:text-[#5c614d]">
                      Zoom Link
                    </a>
                  )}
                </div>
                {event.eventLevels && event.eventLevels.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="text-sm text-gray-600">Nivoi:</span>
                    {event.eventLevels.map((level, index) => (
                      <span key={index} className="bg-[#5c614d]/20 text-[#5c614d] text-xs px-2 py-1 rounded-full">
                        {level}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <button className="bg-[#5c614d] text-white px-3 py-1 rounded text-sm hover:bg-[#5c614d] transition">
                    Priključi se
                  </button>
                  <button className="bg-[#5c614d] text-white px-3 py-1 rounded text-sm hover:bg-[#5c614d] transition">
                    Dodaj v kalendar
                  </button>
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => {
                    setSelectedEvent({
                      ...event,
                      date: event.date ? new Date(event.date).toISOString().split('T')[0] : ''
                    });
                    setShowEditModal(true);
                  }}
                  className="text-[#5c614d] hover:text-[#5c614d] font-medium"
                >
                  Uredi
                </button>
                <button
                  onClick={() => handleDeleteEvent(event.id)}
                  className="text-[#931b1d] hover:text-[#931b1d] font-medium"
                >
                  Obriši
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Event Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
            <button type="button" onClick={() => setShowAddModal(false)} className="absolute top-4 right-4 p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition z-10 bg-white" aria-label="Zatvori">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 pr-8">Dodaj Novi Događaj</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Naslov *</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="Nedeljni sastanak"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Opis *</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  rows="3"
                  placeholder="Nedeljni sastanak tima za planiranje"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Poeni</label>
                <input
                  type="number"
                  value={newEvent.points}
                  onChange={(e) => setNewEvent({ ...newEvent, points: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kod događaja *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newEvent.eventCode}
                    onChange={(e) => setNewEvent({ ...newEvent, eventCode: e.target.value.toUpperCase() })}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent font-mono"
                    placeholder="ABC123"
                  />
                  <button
                    type="button"
                    onClick={() => setNewEvent({ ...newEvent, eventCode: generateEventCode() })}
                    className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                  >
                    Generiši
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Datum (opciono)</label>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vreme (opciono)</label>
                <input
                  type="time"
                  value={newEvent.time}
                  onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Zoom Link (opciono)</label>
                <input
                  type="url"
                  value={newEvent.zoomLink}
                  onChange={(e) => setNewEvent({ ...newEvent, zoomLink: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="https://zoom.us/j/..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nivoi događaja</label>
                <div className="space-y-2">
                  {['osnovni', 'napredni', 'mastermind'].map((level) => (
                    <label key={level} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newEvent.eventLevels.includes(level)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewEvent({ ...newEvent, eventLevels: [...newEvent.eventLevels, level] });
                          } else {
                            setNewEvent({ ...newEvent, eventLevels: newEvent.eventLevels.filter(l => l !== level) });
                          }
                        }}
                        className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                      />
                      <span className="text-sm text-gray-700 capitalize">{level}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={newEvent.isActive}
                  onChange={(e) => setNewEvent({ ...newEvent, isActive: e.target.checked })}
                  className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">Aktivan</label>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewEvent({
                    title: '',
                    description: '',
                    points: 20,
                    eventCode: '',
                    isActive: true,
                    date: '',
                    time: '',
                    zoomLink: '',
                    eventLevels: []
                  });
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Zatvori
              </button>
              <button
                onClick={handleAddEvent}
                disabled={loading}
                className="flex-1 bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d] transition disabled:opacity-50"
              >
                {loading ? 'Dodavanje...' : 'Dodaj'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
            <button type="button" onClick={() => { setShowEditModal(false); setSelectedEvent(null); }} className="absolute top-4 right-4 p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition z-10 bg-white" aria-label="Zatvori">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 pr-8">Uredi Događaj</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Naslov *</label>
                <input
                  type="text"
                  value={selectedEvent.title}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Opis *</label>
                <textarea
                  value={selectedEvent.description}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  rows="3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Poeni</label>
                <input
                  type="number"
                  value={selectedEvent.points}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, points: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kod događaja *</label>
                <input
                  type="text"
                  value={selectedEvent.eventCode}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, eventCode: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Datum (opciono)</label>
                <input
                  type="date"
                  value={selectedEvent.date}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, date: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vreme (opciono)</label>
                <input
                  type="time"
                  value={selectedEvent.time || ''}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, time: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Zoom Link (opciono)</label>
                <input
                  type="url"
                  value={selectedEvent.zoomLink || ''}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, zoomLink: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="https://zoom.us/j/..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nivoi događaja</label>
                <div className="space-y-2">
                  {['osnovni', 'napredni', 'mastermind'].map((level) => (
                    <label key={level} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={(selectedEvent.eventLevels || []).includes(level)}
                        onChange={(e) => {
                          const currentLevels = selectedEvent.eventLevels || [];
                          if (e.target.checked) {
                            setSelectedEvent({ ...selectedEvent, eventLevels: [...currentLevels, level] });
                          } else {
                            setSelectedEvent({ ...selectedEvent, eventLevels: currentLevels.filter(l => l !== level) });
                          }
                        }}
                        className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                      />
                      <span className="text-sm text-gray-700 capitalize">{level}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActiveEdit"
                  checked={selectedEvent.isActive}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, isActive: e.target.checked })}
                  className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                />
                <label htmlFor="isActiveEdit" className="text-sm text-gray-700">Aktivan</label>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedEvent(null);
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Zatvori
              </button>
              <button
                onClick={handleUpdateEvent}
                disabled={loading}
                className="flex-1 bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d] transition disabled:opacity-50"
              >
                {loading ? 'Čuvanje...' : 'Sačuvaj'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Nivo Fokus (level recommendations) admin component
const NIVO_FOKUS_LEVELS = ['Osnovni', 'Napredni', 'Mastermind'];
const emptyColumn = () => ({ items: Array(6).fill(null).map(() => ({ title: '', url: '' })), bottomLinkUrl: '' });

function AdminNivoFokus({ db }) {
  const [selectedLevel, setSelectedLevel] = useState('Osnovni');
  const [data, setData] = useState({ leftColumn: emptyColumn(), rightColumn: emptyColumn() });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'nivoFokus', selectedLevel));
        if (snap.exists() && snap.data()) {
          const d = snap.data();
          const left = d.leftColumn || emptyColumn();
          const right = d.rightColumn || emptyColumn();
          setData({
            leftColumn: {
              items: Array(6).fill(null).map((_, i) => left.items && left.items[i] ? left.items[i] : { title: '', url: '' }),
              bottomLinkUrl: left.bottomLinkUrl || ''
            },
            rightColumn: {
              items: Array(6).fill(null).map((_, i) => right.items && right.items[i] ? right.items[i] : { title: '', url: '' }),
              bottomLinkUrl: right.bottomLinkUrl || ''
            }
          });
        } else {
          setData({ leftColumn: emptyColumn(), rightColumn: emptyColumn() });
        }
      } catch (e) {
        console.error('Error loading nivo fokus:', e);
        setData({ leftColumn: emptyColumn(), rightColumn: emptyColumn() });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [db, selectedLevel]);

  const setLeftItem = (index, field, value) => {
    const next = { ...data };
    next.leftColumn = { ...next.leftColumn, items: [...next.leftColumn.items] };
    next.leftColumn.items[index] = { ...next.leftColumn.items[index], [field]: value };
    setData(next);
  };
  const setRightItem = (index, field, value) => {
    const next = { ...data };
    next.rightColumn = { ...next.rightColumn, items: [...next.rightColumn.items] };
    next.rightColumn.items[index] = { ...next.rightColumn.items[index], [field]: value };
    setData(next);
  };

  const getValidItems = (items) => items.filter((i) => i && (i.title || '').trim());
  const handleSave = async () => {
    const leftItems = getValidItems(data.leftColumn.items);
    const rightItems = getValidItems(data.rightColumn.items);
    if (leftItems.length < 3 || leftItems.length > 6) {
      alert('Leva kolona mora imati između 3 i 6 stavki.');
      return;
    }
    if (rightItems.length < 3 || rightItems.length > 6) {
      alert('Desna kolona mora imati između 3 i 6 stavki.');
      return;
    }
    setSaving(true);
    try {
      await setDoc(doc(db, 'nivoFokus', selectedLevel), {
        leftColumn: {
          items: leftItems,
          bottomLinkUrl: (data.leftColumn.bottomLinkUrl || '').trim()
        },
        rightColumn: {
          items: rightItems,
          bottomLinkUrl: (data.rightColumn.bottomLinkUrl || '').trim()
        }
      });
      alert('Sačuvano.');
    } catch (e) {
      console.error('Error saving nivo fokus:', e);
      alert('Greška pri čuvanju.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500 p-4">Učitavanje...</div>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Nivo fokus – preporuke po nivou</h2>
        <select
          value={selectedLevel}
          onChange={(e) => setSelectedLevel(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
        >
          {NIVO_FOKUS_LEVELS.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#931b1d] text-white px-6 py-2 rounded-lg hover:bg-[#931b1d] transition disabled:opacity-50"
        >
          {saving ? 'Čuvanje...' : 'Sačuvaj'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Moduli i zadaci (3–6 stavki)</h3>
          {data.leftColumn.items.map((item, i) => (
            <div key={`left-${i}`} className="flex gap-2 mb-3">
              <input
                type="text"
                placeholder="Naslov"
                value={item.title || ''}
                onChange={(e) => setLeftItem(i, 'title', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <input
                type="text"
                placeholder="URL"
                value={item.url || ''}
                onChange={(e) => setLeftItem(i, 'url', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          ))}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Link za &quot;Ovde&quot; (mesečna konsultacija)</label>
            <input
              type="text"
              placeholder="https://..."
              value={data.leftColumn.bottomLinkUrl || ''}
              onChange={(e) => setData({ ...data, leftColumn: { ...data.leftColumn, bottomLinkUrl: e.target.value } })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Važni događaji za tvoj nivo (3–6 stavki)</h3>
          {data.rightColumn.items.map((item, i) => (
            <div key={`right-${i}`} className="flex gap-2 mb-3">
              <input
                type="text"
                placeholder="Naslov"
                value={item.title || ''}
                onChange={(e) => setRightItem(i, 'title', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <input
                type="text"
                placeholder="URL"
                value={item.url || ''}
                onChange={(e) => setRightItem(i, 'url', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          ))}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Link za speaker (Jelenin savet)</label>
            <input
              type="text"
              placeholder="https://..."
              value={data.rightColumn.bottomLinkUrl || ''}
              onChange={(e) => setData({ ...data, rightColumn: { ...data.rightColumn, bottomLinkUrl: e.target.value } })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Focus Zones Management Component
const FOCUS_ZONE_SECTION_DEFAULTS = {
  title: 'Dobro došla u zonu fokusa.',
  description: 'Dobro došla u fokus zonu.\nMesto gde prestaju distrakcije i počinju akcije.'
};

const FOCUS_ZONE_LEVELS = ['OSNOVNI', 'NAPREDNI', 'MASTERMIND'];

function AdminFokusZona({ db }) {
  const [focusZones, setFocusZones] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFocusZone, setSelectedFocusZone] = useState(null);
  const [newFocusZone, setNewFocusZone] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    link: '',
    isActive: true,
    eventLevels: []
  });
  const [loading, setLoading] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [sectionHeader, setSectionHeader] = useState({ title: FOCUS_ZONE_SECTION_DEFAULTS.title, description: FOCUS_ZONE_SECTION_DEFAULTS.description });
  const [sectionSaving, setSectionSaving] = useState(false);
  const [showTemaModal, setShowTemaModal] = useState(false);
  const [temaData, setTemaData] = useState({ title: '', section1: '', section2: '', section3: '', section4: '' });
  const [temaSaving, setTemaSaving] = useState(false);

  useEffect(() => {
    loadFocusZones();
    loadSectionHeader();
  }, []);

  const loadSectionHeader = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'settings', 'focusZonaSection'));
      if (docSnap.exists() && docSnap.data()) {
        const d = docSnap.data();
        setSectionHeader({ title: d.title || FOCUS_ZONE_SECTION_DEFAULTS.title, description: d.description ?? FOCUS_ZONE_SECTION_DEFAULTS.description });
      }
    } catch (e) {
      console.error('Error loading focus section header:', e);
    }
  };

  const saveSectionHeader = async () => {
    setSectionSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'focusZonaSection'), { title: sectionHeader.title, description: sectionHeader.description });
      setShowSectionModal(false);
    } catch (e) {
      console.error('Error saving focus section header:', e);
    } finally {
      setSectionSaving(false);
    }
  };

  const loadTemaData = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'settings', 'focusZonaTema'));
      if (docSnap.exists() && docSnap.data()) {
        const d = docSnap.data();
        const fullTitle = (d.title != null && d.title !== '') ? d.title : (`Tema focus zone za ${d.titleSuffix || ''}`.trim());
        setTemaData({
          title: fullTitle,
          section1: d.section1 || '',
          section2: d.section2 || '',
          section3: d.section3 || '',
          section4: d.section4 || ''
        });
      } else {
        setTemaData({ title: 'Tema focus zone za ', section1: '', section2: '', section3: '', section4: '' });
      }
    } catch (e) {
      console.error('Error loading focus zona tema:', e);
    }
  };

  const saveTemaData = async () => {
    setTemaSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'focusZonaTema'), {
        title: temaData.title || '',
        section1: temaData.section1 || '',
        section2: temaData.section2 || '',
        section3: temaData.section3 || '',
        section4: temaData.section4 || ''
      });
      setShowTemaModal(false);
    } catch (e) {
      console.error('Error saving focus zona tema:', e);
    } finally {
      setTemaSaving(false);
    }
  };

  const loadFocusZones = async () => {
    try {
      const focusZonesSnapshot = await getDocs(collection(db, 'focusZones'));
      setFocusZones(focusZonesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading focus zones:', error);
    }
  };

  const handleAddFocusZone = async () => {
    if (!newFocusZone.title || !newFocusZone.description) {
      // console.log('Popunite sva obavezna polja');
      return;
    }

    setLoading(true);
    try {
      const link = newFocusZone.link ? String(newFocusZone.link).trim() : '';
      const normalizedLink = link && !/^https?:\/\//i.test(link) ? `https://${link}` : link;
      const focusZoneData = {
        title: newFocusZone.title,
        description: newFocusZone.description,
        date: newFocusZone.date ? new Date(newFocusZone.date).toISOString() : null,
        time: newFocusZone.time || null,
        link: normalizedLink || null,
        isActive: newFocusZone.isActive !== false,
        eventLevels: newFocusZone.eventLevels || [],
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'focusZones'), focusZoneData);
      
      // console.log('Fokus zona dodana!');
      setShowAddModal(false);
      setNewFocusZone({
        title: '',
        description: '',
        date: '',
        time: '',
        link: '',
        isActive: true,
        eventLevels: []
      });
      loadFocusZones();
    } catch (error) {
      console.error('Error adding focus zone:', error);
      // console.log('Greška prilikom dodavanja fokus zone.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFocusZone = async () => {
    if (!selectedFocusZone.title || !selectedFocusZone.description) {
      // console.log('Popunite sva obavezna polja');
      return;
    }

    setLoading(true);
    try {
      const link = selectedFocusZone.link ? String(selectedFocusZone.link).trim() : '';
      const normalizedLink = link && !/^https?:\/\//i.test(link) ? `https://${link}` : link;
      const focusZoneData = {
        title: selectedFocusZone.title,
        description: selectedFocusZone.description,
        date: selectedFocusZone.date ? new Date(selectedFocusZone.date).toISOString() : null,
        time: selectedFocusZone.time || null,
        link: normalizedLink || null,
        isActive: selectedFocusZone.isActive !== false,
        eventLevels: selectedFocusZone.eventLevels || []
      };

      await updateDoc(doc(db, 'focusZones', selectedFocusZone.id), focusZoneData);
      
      // console.log('Fokus zona ažurirana!');
      setShowEditModal(false);
      setSelectedFocusZone(null);
      loadFocusZones();
    } catch (error) {
      console.error('Error updating focus zone:', error);
      // console.log('Greška prilikom ažuriranja fokus zone.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFocusZone = async (focusZoneId) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovu fokus zonu?')) return;

    try {
      await deleteDoc(doc(db, 'focusZones', focusZoneId));
      // console.log('Fokus zona obrisana!');
      loadFocusZones();
    } catch (error) {
      console.error('Error deleting focus zone:', error);
      // console.log('Greška prilikom brisanja fokus zone.');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Upravljanje Fokus Zonama ({focusZones.length})</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowSectionModal(true)}
            className="border border-gray-400 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition font-medium"
          >
            Uredi naslov i opis
          </button>
          <button
            onClick={async () => { await loadTemaData(); setShowTemaModal(true); }}
            className="border border-gray-400 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition font-medium"
          >
            Uredi teme fokus zone
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-[#931b1d] text-white px-6 py-3 rounded-lg hover:bg-[#931b1d] transition font-medium flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Dodaj Fokus Zonu
          </button>
        </div>
      </div>

      {/* Section header edit modal */}
      {showSectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg relative">
            <button type="button" onClick={() => setShowSectionModal(false)} className="absolute top-4 right-4 p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition" aria-label="Zatvori">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 pr-8">Naslov i opis Fokus zone</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Naslov</label>
                <input
                  type="text"
                  value={sectionHeader.title}
                  onChange={(e) => setSectionHeader({ ...sectionHeader, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="Dobro došla u zonu fokusa."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Opis</label>
                <textarea
                  value={sectionHeader.description}
                  onChange={(e) => setSectionHeader({ ...sectionHeader, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  rows={4}
                  placeholder="Dobro došla u fokus zonu..."
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowSectionModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition">Zatvori</button>
              <button onClick={saveSectionHeader} disabled={sectionSaving} className="flex-1 bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d] transition disabled:opacity-50">{sectionSaving ? 'Čuvanje...' : 'Sačuvaj'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Tema focus zone edit modal */}
      {showTemaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl my-8 relative">
            <button type="button" onClick={() => setShowTemaModal(false)} className="absolute top-4 right-4 p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition" aria-label="Zatvori">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 pr-8">Teme fokus zone</h3>
            <div className="space-y-4 mb-6 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Naslov sekcije (ceo naslov)</label>
                <input
                  type="text"
                  value={temaData.title}
                  onChange={(e) => setTemaData({ ...temaData, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="npr. Tema focus zone za ovu nedelju"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vežba vizualizacije</label>
                <textarea
                  value={temaData.section1}
                  onChange={(e) => setTemaData({ ...temaData, section1: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  rows={4}
                  placeholder="Opis..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Predlog teme za pisanje za B2C | Digitalni proizvod</label>
                <textarea
                  value={temaData.section2}
                  onChange={(e) => setTemaData({ ...temaData, section2: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  rows={4}
                  placeholder="Opis..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Predlog teme za pisanje za B2C | Fizički proizvod</label>
                <textarea
                  value={temaData.section3}
                  onChange={(e) => setTemaData({ ...temaData, section3: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  rows={4}
                  placeholder="Opis..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Predlog teme za pisanje za B2B</label>
                <textarea
                  value={temaData.section4}
                  onChange={(e) => setTemaData({ ...temaData, section4: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  rows={4}
                  placeholder="Opis..."
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowTemaModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition">Zatvori</button>
              <button onClick={saveTemaData} disabled={temaSaving} className="flex-1 bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d] transition disabled:opacity-50">{temaSaving ? 'Čuvanje...' : 'Sačuvaj'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4">
        {focusZones.map(focusZone => (
          <div key={focusZone.id} className="bg-white rounded-xl shadow p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-800">{focusZone.title}</h3>
                  {focusZone.isActive && (
                    <span className="bg-[#5c614d]/20 text-[#5c614d] text-xs px-2 py-1 rounded-full font-medium">
                      Aktivan
                    </span>
                  )}
                </div>
                <p className="text-gray-600 text-sm mb-2">{focusZone.description}</p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-3">
                  {focusZone.date && (
                    <span>Datum: {new Date(focusZone.date).toLocaleDateString('sr-RS')}</span>
                  )}
                  {focusZone.time && (
                    <span>Vreme: {focusZone.time}</span>
                  )}
                  {focusZone.link && (
                    <a href={focusZone.link.startsWith('http') ? focusZone.link : `https://${focusZone.link}`} target="_blank" rel="noopener noreferrer" className="text-[#5c614d] hover:text-[#5c614d]">
                      Link
                    </a>
                  )}
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => {
                    setSelectedFocusZone({
                      ...focusZone,
                      date: focusZone.date ? new Date(focusZone.date).toISOString().split('T')[0] : '',
                      eventLevels: focusZone.eventLevels || []
                    });
                    setShowEditModal(true);
                  }}
                  className="text-[#5c614d] hover:text-[#5c614d] font-medium"
                >
                  Uredi
                </button>
                <button
                  onClick={() => handleDeleteFocusZone(focusZone.id)}
                  className="text-[#931b1d] hover:text-[#931b1d] font-medium"
                >
                  Obriši
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Focus Zone Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
            <button type="button" onClick={() => setShowAddModal(false)} className="absolute top-4 right-4 p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition z-10 bg-white" aria-label="Zatvori">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 pr-8">Dodaj Novu Fokus Zonu</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Naslov *</label>
                <input
                  type="text"
                  value={newFocusZone.title}
                  onChange={(e) => setNewFocusZone({ ...newFocusZone, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="Anti-scroll zona stvaranja"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Opis *</label>
                <RichTextEditor
                  value={newFocusZone.description}
                  onChange={(html) => setNewFocusZone({ ...newFocusZone, description: html })}
                  placeholder="Zajednički i vođen rad na sadržajima..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nivo (za koga je događaj)</label>
                <div className="flex flex-wrap gap-3">
                  {FOCUS_ZONE_LEVELS.map((level) => (
                    <label key={level} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={(newFocusZone.eventLevels || []).includes(level)}
                        onChange={(e) => {
                          const current = newFocusZone.eventLevels || [];
                          setNewFocusZone({
                            ...newFocusZone,
                            eventLevels: e.target.checked ? [...current, level] : current.filter(l => l !== level)
                          });
                        }}
                        className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                      />
                      {level}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Datum (opciono)</label>
                <input
                  type="date"
                  value={newFocusZone.date}
                  onChange={(e) => setNewFocusZone({ ...newFocusZone, date: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vreme (opciono)</label>
                <input
                  type="time"
                  value={newFocusZone.time}
                  onChange={(e) => setNewFocusZone({ ...newFocusZone, time: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Link (opciono)</label>
                <input
                  type="url"
                  value={newFocusZone.link}
                  onChange={(e) => setNewFocusZone({ ...newFocusZone, link: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="https://zoom.us/j/... ili bilo koji link"
                />
                <p className="text-xs text-gray-500 mt-1">Link za Zoom ili drugu platformu. Ako nema https://, biće dodato automatski.</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={newFocusZone.isActive}
                  onChange={(e) => setNewFocusZone({ ...newFocusZone, isActive: e.target.checked })}
                  className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">Aktivan</label>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewFocusZone({
                    title: '',
                    description: '',
                    date: '',
                    time: '',
                    link: '',
                    isActive: true,
                    eventLevels: []
                  });
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Zatvori
              </button>
              <button
                onClick={handleAddFocusZone}
                disabled={loading}
                className="flex-1 bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d] transition disabled:opacity-50"
              >
                {loading ? 'Dodavanje...' : 'Dodaj'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Focus Zone Modal */}
      {showEditModal && selectedFocusZone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
            <button type="button" onClick={() => { setShowEditModal(false); setSelectedFocusZone(null); }} className="absolute top-4 right-4 p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition z-10 bg-white" aria-label="Zatvori">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 pr-8">Uredi Fokus Zonu</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Naslov *</label>
                <input
                  type="text"
                  value={selectedFocusZone.title}
                  onChange={(e) => setSelectedFocusZone({ ...selectedFocusZone, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Opis *</label>
                <RichTextEditor
                  value={selectedFocusZone.description}
                  onChange={(html) => setSelectedFocusZone({ ...selectedFocusZone, description: html })}
                  placeholder="Zajednički i vođen rad na sadržajima..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nivo (za koga je događaj)</label>
                <div className="flex flex-wrap gap-3">
                  {FOCUS_ZONE_LEVELS.map((level) => (
                    <label key={level} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={(selectedFocusZone.eventLevels || []).includes(level)}
                        onChange={(e) => {
                          const current = selectedFocusZone.eventLevels || [];
                          setSelectedFocusZone({
                            ...selectedFocusZone,
                            eventLevels: e.target.checked ? [...current, level] : current.filter(l => l !== level)
                          });
                        }}
                        className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                      />
                      {level}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Datum (opciono)</label>
                <input
                  type="date"
                  value={selectedFocusZone.date}
                  onChange={(e) => setSelectedFocusZone({ ...selectedFocusZone, date: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vreme (opciono)</label>
                <input
                  type="time"
                  value={selectedFocusZone.time || ''}
                  onChange={(e) => setSelectedFocusZone({ ...selectedFocusZone, time: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Link (opciono)</label>
                <input
                  type="url"
                  value={selectedFocusZone.link || ''}
                  onChange={(e) => setSelectedFocusZone({ ...selectedFocusZone, link: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                  placeholder="https://zoom.us/j/... ili bilo koji link"
                />
                <p className="text-xs text-gray-500 mt-1">Ako nema https://, biće dodato automatski pri čuvanju.</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActiveEdit"
                  checked={selectedFocusZone.isActive}
                  onChange={(e) => setSelectedFocusZone({ ...selectedFocusZone, isActive: e.target.checked })}
                  className="w-4 h-4 text-[#931b1d] rounded focus:ring-[#931b1d]"
                />
                <label htmlFor="isActiveEdit" className="text-sm text-gray-700">Aktivan</label>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedFocusZone(null);
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Zatvori
              </button>
              <button
                onClick={handleUpdateFocusZone}
                disabled={loading}
                className="flex-1 bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d] transition disabled:opacity-50"
              >
                {loading ? 'Čuvanje...' : 'Sačuvaj'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Points Management Component
function AdminPoints({ db, settings }) {
  const [pointsData, setPointsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPointsData();
  }, [settings]);

  const loadPointsData = async () => {
    try {
      setLoading(true);
      const pointsSnapshot = await getDocs(
        query(
          collection(db, 'quarters', settings.currentQuarter, 'points'),
          orderBy('totalPoints', 'desc')
        )
      );
      
      const pointsWithMembers = await Promise.all(
        pointsSnapshot.docs.map(async (pointDoc) => {
          const memberDoc = await getDoc(doc(db, 'members', pointDoc.data().memberId));
          return {
            id: pointDoc.id,
            ...pointDoc.data(),
            member: memberDoc.exists() ? memberDoc.data() : null
          };
        })
      );
      
      setPointsData(pointsWithMembers);
    } catch (error) {
      console.error('Error loading points data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustPoints = async (memberId, adjustment) => {
    try {
      const pointsRef = doc(db, 'quarters', settings.currentQuarter, 'points', memberId);
      const pointsDoc = await getDoc(pointsRef);
      
      if (pointsDoc.exists()) {
        const currentPoints = pointsDoc.data().totalPoints || 0;
        const newPoints = Math.max(0, currentPoints + adjustment);
        
        await updateDoc(pointsRef, {
          totalPoints: newPoints,
          lastUpdated: new Date().toISOString()
        });
        
        // Add activity record
        await addDoc(collection(db, 'quarters', settings.currentQuarter, 'activities'), {
          memberId,
          type: 'admin_adjustment',
          points: adjustment,
          description: `Admin prilagodba: ${adjustment > 0 ? '+' : ''}${adjustment} poena`,
          timestamp: new Date().toISOString()
        });
        
        // console.log(`Poeni prilagođeni: ${adjustment > 0 ? '+' : ''}${adjustment}`);
        loadPointsData();
      }
    } catch (error) {
      console.error('Error adjusting points:', error);
      // console.log('Greška prilikom prilagođavanja poena.');
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  if (loading) {
  return (
    <div className="p-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">Upravljanje Bodovima</h2>
        <div className="text-center py-8">Učitavanje...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Upravljanje Bodovima</h2>
        <div className="text-sm text-gray-600">
          Kvartal: {settings.currentQuarter}
        </div>
      </div>

      {pointsData.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <p className="text-gray-500 mb-2">Nema podataka za trenutni kvartal</p>
          <p className="text-gray-400 text-sm">Bodovi će se prikazati kada članovi počnu sa aktivnostima</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Član</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Poeni</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Akcije</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pointsData.map((entry, index) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {entry.member?.avatarUrl ? (
                        <img 
                          src={entry.member.avatarUrl} 
                          alt={getDisplayName(entry.member) || 'User'} 
                          className="w-10 h-10 rounded-full object-cover" 
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#931b1d]/30 flex items-center justify-center text-[#931b1d] font-semibold">
                          {getInitials(entry.member?.name)}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-gray-800">
                        {entry.member?.name || 'Nepoznat član'}
                        </div>
                        <div className="text-sm text-gray-500">#{index + 1}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-xl font-bold text-[#931b1d]">{entry.totalPoints || 0}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => handleAdjustPoints(entry.memberId, 10)}
                        className="text-[#5c614d] hover:text-[#5c614d] font-medium text-sm"
                      >
                        +10
                      </button>
                      <button
                        onClick={() => handleAdjustPoints(entry.memberId, -10)}
                        className="text-[#931b1d] hover:text-[#931b1d] font-medium text-sm"
                      >
                        -10
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Migrate activities from Q4-2025 to Q1-2026 when activity timestamp is in Jan–Mar 2026. Updates points in both quarters.
const migrateQ42025ActivitiesToQ12026 = async (database) => {
  const sourceQuarter = 'Q4-2025';
  const targetQuarter = 'Q1-2026';
  const q1Start = new Date('2026-01-01T00:00:00.000Z').getTime();
  const q1End = new Date('2026-03-31T23:59:59.999Z').getTime();
  await ensureQuarterExists(database, targetQuarter);
  const activitiesSnap = await getDocs(collection(database, 'quarters', sourceQuarter, 'activities'));
  let migrated = 0;
  for (const d of activitiesSnap.docs) {
    const data = d.data();
    const ts = data.timestamp ? new Date(data.timestamp).getTime() : 0;
    if (ts < q1Start || ts > q1End) continue;
    const pointsToMove = data.points || 0;
    const memberId = data.memberId;
    const dataToWrite = { ...data };
    delete dataToWrite.id;
    await addDoc(collection(database, 'quarters', targetQuarter, 'activities'), dataToWrite);
    await deleteDoc(d.ref);
    if (memberId && pointsToMove) {
      const pointsRefQ4 = doc(database, 'quarters', sourceQuarter, 'points', memberId);
      const pointsRefQ1 = doc(database, 'quarters', targetQuarter, 'points', memberId);
      const q4Snap = await getDoc(pointsRefQ4);
      const q1Snap = await getDoc(pointsRefQ1);
      if (q4Snap.exists()) {
        const current = q4Snap.data().totalPoints || 0;
        await updateDoc(pointsRefQ4, { totalPoints: Math.max(0, current - pointsToMove), lastUpdated: new Date().toISOString() });
      }
      if (q1Snap.exists()) {
        const current = q1Snap.data().totalPoints || 0;
        await updateDoc(pointsRefQ1, { totalPoints: current + pointsToMove, lastUpdated: new Date().toISOString() });
      } else {
        await setDoc(pointsRefQ1, { memberId, totalPoints: pointsToMove, lastUpdated: new Date().toISOString() });
      }
    }
    migrated += 1;
  }
  return migrated;
};

// Copy Q4-2025 points into Q1-2026 so the rang list shows current quarter ranking. Only creates Q1 doc if missing (does not overwrite existing Q1 points).
const copyQ42025PointsToQ12026 = async (database) => {
  const sourceQuarter = 'Q4-2025';
  const targetQuarter = 'Q1-2026';
  await ensureQuarterExists(database, targetQuarter);
  const pointsSnap = await getDocs(collection(database, 'quarters', sourceQuarter, 'points'));
  let copied = 0;
  for (const d of pointsSnap.docs) {
    const memberId = d.id;
    const totalPoints = d.data().totalPoints ?? 0;
    const targetRef = doc(database, 'quarters', targetQuarter, 'points', memberId);
    const targetSnap = await getDoc(targetRef);
    if (!targetSnap.exists()) {
      await setDoc(targetRef, { memberId, totalPoints, lastUpdated: new Date().toISOString() });
      copied += 1;
    }
  }
  return copied;
};

// Settings Management Component
function AdminSettings({ db, settings, onRefresh }) {
  const [localSettings, setLocalSettings] = useState(settings);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [copyingPoints, setCopyingPoints] = useState(false);
  const [copyPointsResult, setCopyPointsResult] = useState(null);

  const handleMigrateQ42025ToQ12026 = async () => {
    if (!window.confirm('Prebaciti sve aktivnosti iz Q4-2025 čiji je datum u Q1-2026 (januar–mart 2026) u Q1-2026? Ažuriraće se i poeni u oba kvartala.')) return;
    setMigrating(true);
    setMigrationResult(null);
    try {
      const count = await migrateQ42025ActivitiesToQ12026(db);
      setMigrationResult(`Prebačeno ${count} aktivnosti u Q1-2026.`);
      onRefresh();
    } catch (e) {
      console.error(e);
      setMigrationResult('Greška: ' + (e.message || String(e)));
    } finally {
      setMigrating(false);
    }
  };

  const handleCopyQ4PointsToQ1 = async () => {
    if (!window.confirm('Kopirati ukupne poene iz Q4-2025 u Q1-2026 za članove koji još nemaju poene u Q1-2026? Rang lista će prikazati trenutni kvartal (Q1-2026).')) return;
    setCopyingPoints(true);
    setCopyPointsResult(null);
    try {
      const count = await copyQ42025PointsToQ12026(db);
      setCopyPointsResult(`Kopirano ${count} članova u Q1-2026. Rang lista sada prikazuje poene za trenutni kvartal.`);
      onRefresh();
    } catch (e) {
      console.error(e);
      setCopyPointsResult('Greška: ' + (e.message || String(e)));
    } finally {
      setCopyingPoints(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'settings', 'main'), localSettings);
      // console.log('Postavke sačuvane!');
      onRefresh();
    } catch (error) {
      console.error('Error saving settings:', error);
      // console.log('Greška prilikom čuvanja postavki.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Postavke Sistema</h2>
        <button
          onClick={handleSaveSettings}
          disabled={loading}
          className="bg-[#931b1d] text-white px-6 py-3 rounded-lg hover:bg-[#931b1d] transition font-medium disabled:opacity-50"
        >
          {loading ? 'Čuvanje...' : 'Sačuvaj Postavke'}
        </button>
      </div>

      <div className="space-y-6">
        {/* Basic Settings */}
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Osnovne Postavke</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Zajednička lozinka</label>
              <input
                type="password"
                value={localSettings.sharedPasscode}
                onChange={(e) => setLocalSettings({ ...localSettings, sharedPasscode: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Trenutni kvartal</label>
              <input
                type="text"
                value={localSettings.currentQuarter}
                onChange={(e) => setLocalSettings({ ...localSettings, currentQuarter: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                placeholder={getCurrentQuarter()}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Admin email</label>
              <input
                type="email"
                value={localSettings.adminEmail}
                onChange={(e) => setLocalSettings({ ...localSettings, adminEmail: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Email za admin prijavu (mora biti član sa Admin privilegijama)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Admin lozinka</label>
              <input
                type="password"
                value={localSettings.adminPassword ?? ''}
                onChange={(e) => setLocalSettings({ ...localSettings, adminPassword: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                placeholder="Prazno = nije podešena"
              />
              <p className="text-xs text-gray-500 mt-1">Lozinka za Admin link na stranici za prijavu. Čuva se u Firestore (settings/main).</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Maksimalni dnevni focus poeni</label>
              <input
                type="number"
                value={localSettings.maxDailyFocusPoints}
                onChange={(e) => setLocalSettings({ ...localSettings, maxDailyFocusPoints: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                min="1"
              />
            </div>
          </div>
        </div>

        {/* Data migration: Q4-2025 → Q1-2026 */}
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Migracija podataka</h3>
          <p className="text-sm text-gray-600 mb-3">
            Rang lista uvek prikazuje poene za <strong>trenutni kvartal</strong> (npr. Q1-2026). Da bi se prikazali članovi, u Q1-2026 moraju postojati poeni.
          </p>
          <p className="text-sm text-gray-600 mb-3">
            <strong>Kopiraj poene iz Q4-2025 u Q1-2026:</strong> za svakog člana koji ima poene u Q4-2025 a nema zapis u Q1-2026, kreira se zapis sa istim ukupnim poenima. Rang lista će odmah prikazati rangiranje za trenutni kvartal.
          </p>
          <button
            type="button"
            onClick={handleCopyQ4PointsToQ1}
            disabled={copyingPoints}
            className="bg-[#931b1d] text-white px-4 py-2 rounded-lg hover:bg-[#931b1d]/90 transition font-medium disabled:opacity-50 mr-3"
          >
            {copyingPoints ? 'Kopiranje...' : 'Kopiraj Q4-2025 poene u Q1-2026'}
          </button>
          {copyPointsResult && (
            <p className={`mt-3 text-sm ${copyPointsResult.startsWith('Greška') ? 'text-red-600' : 'text-gray-700'}`}>
              {copyPointsResult}
            </p>
          )}
          <p className="text-sm text-gray-500 mt-4 mb-3">
            <strong>Prebaci aktivnosti</strong> (datum u jan–mart 2026): prebaci aktivnosti iz Q4-2025 čiji je datum u Q1-2026 u kvartal Q1-2026 i ažuriraj poene u oba kvartala.
          </p>
          <button
            type="button"
            onClick={handleMigrateQ42025ToQ12026}
            disabled={migrating}
            className="bg-[#5c614d] text-white px-4 py-2 rounded-lg hover:bg-[#5c614d]/90 transition font-medium disabled:opacity-50"
          >
            {migrating ? 'Migracija...' : 'Prebaci Q4-2025 → Q1-2026 (aktivnosti sa datumom u Q1-2026)'}
          </button>
          {migrationResult && (
            <p className={`mt-3 text-sm ${migrationResult.startsWith('Greška') ? 'text-red-600' : 'text-gray-700'}`}>
              {migrationResult}
            </p>
          )}
        </div>

        {/* Points Configuration */}
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Konfiguracija Poena</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mikro-akcija</label>
              <input
                type="number"
                value={localSettings.pointsConfig.challenge}
                onChange={(e) => setLocalSettings({
                  ...localSettings,
                  pointsConfig: { ...localSettings.pointsConfig, challenge: parseInt(e.target.value) }
                })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grupni poziv</label>
              <input
                type="number"
                value={localSettings.pointsConfig.groupCall}
                onChange={(e) => setLocalSettings({
                  ...localSettings,
                  pointsConfig: { ...localSettings.pointsConfig, groupCall: parseInt(e.target.value) }
                })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grupna aktivnost</label>
              <input
                type="number"
                value={localSettings.pointsConfig.groupActivity}
                onChange={(e) => setLocalSettings({
                  ...localSettings,
                  pointsConfig: { ...localSettings.pointsConfig, groupActivity: parseInt(e.target.value) }
                })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Radionica</label>
              <input
                type="number"
                value={localSettings.pointsConfig.workshop}
                onChange={(e) => setLocalSettings({
                  ...localSettings,
                  pointsConfig: { ...localSettings.pointsConfig, workshop: parseInt(e.target.value) }
                })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Focus sesija</label>
              <input
                type="number"
                value={localSettings.pointsConfig.focusSession}
                onChange={(e) => setLocalSettings({
                  ...localSettings,
                  pointsConfig: { ...localSettings.pointsConfig, focusSession: parseInt(e.target.value) }
                })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#931b1d] focus:border-transparent"
                min="1"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
